import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import momentTimezonePlugin from '@fullcalendar/moment-timezone'
import {
  Button,
  Avatar,
  Row,
  Form,
  Input,
  DatePicker,
  Select,
  message,
  Typography,
  Space,
  Tag,
  Divider,
  InputNumber,
  Drawer,
  Tooltip,
  Modal,
  Dropdown,
  Checkbox,
  Spin
} from 'antd'
import { DatePicker as MobileDatePicker } from 'antd-mobile'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import api from '../utils/api'
import { PHOTO_URL } from '../utils/apiutils'
import {
  getCustomerDisplayData,
  resolvePlatformUser
} from '../utils/customerDisplay'
import { useNavigate } from 'react-router-dom'
import {
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  PlusCircleOutlined,
  DollarOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  DownOutlined,
  EyeOutlined,
  EditOutlined
} from '@ant-design/icons'
import { PiUsersFill } from 'react-icons/pi'
import dayjs from 'dayjs'
import { now as tzNow, inTz, getBusinessTimezone } from '../utils/timezone'
// dayjs 的 utc / timezone / customParseFormat 外掛統一在 utils/timezone 裡 extend，
// 由 src/index.js 在最前面載入，這裡不需要再 extend 一次

const { Text, Title } = Typography

// Helper: derive display name from booking (services first)
const getBookingDisplayName = (booking) => {
  if (booking?.services?.length) {
    const names = booking.services.map((s) => s.serviceName).filter(Boolean)
    if (names.length) return names.join(' + ')
  }
  if (booking?.productName) return booking.productName
  return '（未註明服務）'
}

/**
 * 週／日視圖每 30 分鐘時段的列高（px），必須與 StyledCalendar 裡
 * .fc .fc-timegrid-slot 的 height 一致 —— 預約卡片靠這個值回推自己
 * 放得下幾行，兩邊對不上就會出現「字被切掉」或「留白過多」。
 */
const TIMEGRID_SLOT_HEIGHT = { desktop: 40, narrow: 34 }
const NARROW_BREAKPOINT = 768

const StyledCalendar = styled.div`
  background: #fff;
  height: 100%;
  display: flex;
  flex-direction: column;
  a {
    color: #666;
    font-weight: 800;
  }

  .fc {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
      sans-serif;
  }

  // 移除外框邊框
  .fc-theme-standard .fc-scrollgrid {
    border: none !important;
  }

  // 保持內部分隔線但使用較淺的顏色
  .fc-theme-standard td,
  .fc-theme-standard th {
    border: 1px solid #f5f5f5 !important;
    border-radius: 10px !important;
  }

  // 非當月日期的背景色
  .fc-day-other {
    background-color: #fafafa !important;

    .fc-daygrid-day-top {
      opacity: 0.5; // 讓日期數字顯示更淡
    }
  }

  // 日期格子之間的分隔
  .fc-daygrid-day {
    border: 1px solid #f5f5f5 !important;
  }

  // 週/日視圖的時間格線
  .fc-timegrid-slot {
    border-bottom: 1px solid #f5f5f5 !important;
  }

  /* 週／日視圖的時段列拉高。
     預設的 1.5em 只放得下一行字，是月視圖那種「全部擠一行」排法的來源；
     這兩個視圖本來就是要看細節的，列高夠了卡片才有地方寫客名與設計師。

     用固定 px 而不是 em：卡片要靠這個高度回推「這張卡放得下幾行」，
     em 會隨繼承的字級浮動，算出來的行數就會跟畫面對不上（結果就是
     30 分鐘的預約被塞進兩行、字被切掉）。改動這裡請同步改
     TIMEGRID_SLOT_HEIGHT。 */
  .fc .fc-timegrid-slot {
    height: 40px;
  }

  /* 卡片本身負責留白與截斷，FullCalendar 的內距會干擾多行排版 */
  .fc-timegrid-event .fc-event-main,
  .fc-timegrid-event .fc-event-main-frame {
    padding: 0;
  }

  .fc-timegrid-event {
    box-shadow: none;
  }

  /* 日視圖一欄獨佔整個寬度，服務名可以放到兩行再截斷 */
  .booking-card-service {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal !important;
    overflow: hidden;
  }

  /* 現在時間線用品牌色，跟預約卡片的淡色區分開 */
  .fc .fc-timegrid-now-indicator-line {
    border-color: #e36882;
    border-width: 2px 0 0;
  }

  .fc .fc-timegrid-now-indicator-arrow {
    border-color: #e36882;
    border-top-color: transparent;
    border-bottom-color: transparent;
  }

  @media (max-width: 768px) {
    .fc .fc-timegrid-slot {
      height: 34px;
    }
  }

  // 週/日視圖的垂直分隔線
  .fc-timegrid-col {
    border-right: 1px solid #f5f5f5 !important;
  }

  // 移除最外層容器邊框
  .fc-view-harness {
    border: none !important;
  }

  .fc-toolbar-title {
    font-size: 20px;
    font-weight: 600;
    color: #333;
  }

  // 其他樣式保持不變...
  .fc-button-primary {
    background: #fff !important;
    border: none !important;
    color: #666 !important;
    border-radius: 8px !important;
    padding: 6px 16px !important;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    &:hover {
      background: #f9edf2 !important;
      color: #d5728a !important;
    }

    &.fc-button-active {
      background: #f9edf2 !important;
      color: #d5728a !important;
      font-weight: 600;
    }
  }

  /* 今天的底色。
     這裡刻意用半透明色而不是 opacity —— opacity < 1 會讓「今天」這個 td
     變成一個新的 stacking context，週／日視圖的預約卡片（.fc-timegrid-col-events
     的 z-index:3）就被關在裡面、壓不過 z-index:1 的時間格線，結果是今天的
     預約點不到，點擊會穿透成「新增預約」。改用 rgba 維持一樣的視覺。 */
  .fc-day-today {
    background: rgba(249, 237, 242, 0.8) !important;
  }

  /* 週/日視圖：回到接近官方的清爽樣式，但維持配色與橫向滑動 */
  .fc-timegrid-col-frame {
    background: #fff;
    border-left: 1px solid #ececec;
  }

  .fc-timegrid-col:last-child .fc-timegrid-col-frame {
    border-right: 1px solid #ececec;
  }

  .fc-day-today .fc-timegrid-col-frame,
  .fc-day-today .fc-scrollgrid-sync-table {
    background: rgba(227, 104, 130, 0.08) !important;
    box-shadow: inset 0 0 0 1px rgba(227, 104, 130, 0.18);
  }

  /**
   * 週視圖的橫向捲動要發生在 FullCalendar 自己的 scroller 上。
   *
   * ⚠️ 原本寫的是「.fc-timegrid-body .fc-scroller」—— 方向反了。實際 DOM 是
   * scroller 包著 timegrid-body，不是 body 包著 scroller，所以那條選擇器
   * 從來沒有命中過；以前之所以能拉，是因為表格把整個 .fc 撐爆、橫向捲動
   * 漏到 document 上（連上方的日期列和標題都會被推走）。
   * 一旦把外層改成不溢出，沒有這條就會變成「後面幾天直接被裁掉、拉不到」。
   */
  .fc .fc-timegrid .fc-scroller-liquid-absolute,
  .fc .fc-timegrid-header .fc-scroller {
    overflow-x: auto !important;
  }

  .fc-timegrid-body table,
  .fc-timegrid-header table {
    min-width: 1000px;
  }

  /**
   * 時間軸固定在左邊。
   *
   * 週視圖一定會橫向捲動（七欄放不進手機寬度），但時間軸原本是表格的第一欄，
   * 一往右拉就整條捲走 —— 剩下的卡片就沒有任何時間參考，只能一張一張讀
   * 卡片上自己的時間。釘住之後怎麼拉都看得到現在在幾點。
   * 背景要填實色，否則捲到下面的卡片會透出來疊在數字上。
   */
  /**
   * 試過把時間軸釘在左邊（position: sticky），但 FullCalendar 的時間標籤是
   * 表格 <td>，釘住之後整欄的文字就不再繪出來；要讓它蓋過卡片還得把
   * .fc-timegrid-slots 整層的 z-index 抬到卡片之上，副作用是格線會壓在
   * 卡片上。兩個問題都不值得為了一個裝飾性的改善去硬幹，所以先不做。
   * 改用「把一週壓進 760px、少捲一點」來降低時間軸捲走的機會。
   */

  @media (max-width: 768px) {
    /**
     * 手機版的週視圖寬度。
     *
     * 原本是 1200px —— 比桌機的 1000px 還寬，390px 的手機等於要左右拉三個
     * 畫面才看得完一週。改成 760px（一欄約 100px），大約一個半畫面。
     */
    .fc-timegrid-body table,
    .fc-timegrid-header table {
      min-width: 760px;
    }

    /**
     * 日視圖只有一欄，不該橫向捲動。
     * 上面的 min-width 原本無差別套用，單日畫面也被撐成 1200px，
     * 手機上看一天要左右拉，完全沒有道理。
     */
    .fc-timeGridDay-view .fc-timegrid-body table,
    .fc-timeGridDay-view .fc-timegrid-header table {
      min-width: 0;
    }
  }
  .fc-col-header-cell-cushion {
    padding: 0px;
    font-size: 12px;
  }

  .fc .fc-timegrid {
    min-width: 700px;
  }

  /**
   * 手機版取消 timegrid 的 700px 下限。
   *
   * 那個下限會把 FullCalendar 的根容器撐得比畫面寬，於是橫向捲動跑到
   * 整個頁面上 —— 往右拉的時候連上方的日期列、標題、月／週／日切換
   * 都會被推出畫面。取消之後容器貼齊畫面寬度，捲動關回日曆自己的
   * scroller 裡，頁面本身不再左右晃。
   */
  @media (max-width: 768px) {
    .fc .fc-timegrid {
      min-width: 0;
    }
  }
  .fc-scroller-harness {
    overflow-x: auto;
  }
  .fc-event-main {
    padding: 0px;
  }

  .fc-event {
    border-radius: 6px;
    border: none !important;
    padding: 0px;
    margin: 1px 0;
    cursor: pointer;
    transition: transform 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    &:hover {
      transform: scale(1.02);
    }
  }

  // 确保事件标题容器能够正确截断长文本
  .fc-event-title-container {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  /* 休假事件：手機版格子只有 50 幾 px，放不下完整區間，
     維持一行、超出的部分用 ... 收掉（完整時間看 title 或點開詳情） */
  @media (max-width: 768px) {
    .dayoff-event {
      display: block !important;
      font-size: 9px !important;
      line-height: 1.4 !important;
    }
  }

  /* 字級與內距對齊預約卡片（那邊是 0.8em / 2px 4px）——
     標籤本來寫死 12px，比同一格裡的預約明顯大一號，看起來像兩套系統。 */
  .day-off-tag {
    display: block;
    max-width: 100%;
    margin-top: 2px;
    padding: 1px 4px;
    border-radius: 5px;
    font-size: 0.8em;
    color: #b65066;
    background: #fde8ed;
    line-height: 1.3;
    word-break: break-all;

    @media (max-width: 768px) {
      font-size: 0.72em;
      padding: 0 2px;
    }
  }

  .day-off-tag.employee {
    color: #6b4fa3;
    background: #f0e8ff;
  }

  /* 時段文字：比名字淡一階，讓「誰休假」仍是視覺重點 */
  .day-off-tag .day-off-time {
    opacity: 0.75;
    font-variant-numeric: tabular-nums;
  }

  /* 上班班別：跟休假標籤同一套版型，靠左側色條區分是哪一個班別
     （顏色沿用班表設定，兩邊看起來才是同一件事） */
  .shift-tag {
    display: block;
    max-width: 100%;
    margin-top: 2px;
    padding: 1px 4px;
    border-radius: 5px;
    border-left: 3px solid #c9c9c9;
    font-size: 0.8em;
    line-height: 1.3;
    word-break: break-all;

    @media (max-width: 768px) {
      font-size: 0.72em;
      padding: 0 2px;
    }
  }

  .shift-tag .shift-code {
    font-weight: 600;
  }

  /* 名字比班別代號淡一階：先看得出「有哪幾個班」，再看是誰 */
  .shift-tag .shift-names {
    opacity: 0.8;
  }

  /* 週／日視圖的全天列：標籤跟日期數字擠在同一列，而那一列是 row-reverse 的
     flex，預設會整包貼到右邊。月視圖靠右是對的（跟著日期數字），但週／日的
     全天列根本沒有日期數字，靠右只會讓它離自己那一欄的中線很遠。改成置中。 */
  .fc-timeGridWeek-view .fc-daygrid-day-top,
  .fc-timeGridDay-view .fc-daygrid-day-top {
    justify-content: center;
  }

  /* 週／日視圖的班別與休假標籤會出現兩次，原因在 FullCalendar 自己：
     只要有給 dayCellContent，v6 的 TimeCol 除了全天列之外，還會把同一份
     內容再畫一次到每個時間欄的頂端（.fc-timegrid-col-misc，見
     @fullcalendar/timegrid internal.js 的 hasCustomDayCellContent 判斷）。
     月視圖沒有時間欄，所以只有週／日會重複。
     全天列那份才是我們要的（置中、跟日期對齊），時間欄這份整個關掉 ——
     它還會把時間軸往下推，擠壓到最早的那幾筆預約。 */
  .fc-timegrid-col-misc {
    display: none;
  }

  .day-off-tag.clickable {
    cursor: pointer;
    transition: filter 0.15s ease;
  }

  .day-off-tag.clickable:hover {
    filter: brightness(0.95);
  }

  @media (max-width: 768px) {
    padding: 0;
    margin: 0;

    .fc-toolbar-title {
      font-size: 16px;
    }

    .fc-button {
      padding: 8px 12px !important;
      margin: 4px 4px 0 4px !important;
      font-size: 14px !important;
    }

    .fc-view-harness {
      height: calc(100vh - 200px) !important;
      min-height: 400px !important;
    }
  }

  @media (width: 820px) {
    .fc {
      height: calc(100vh - 200px) !important;
    }

    .fc-view-harness {
      height: auto !important;
      min-height: 600px !important;
    }
  }
`

const EmployeeBar = styled(Row)`
  margin-bottom: 20px;
  justify-content: end;
`

const StyledAvatar = styled(Avatar)`
  margin-right: 10px;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`

/** 預約狀態文案。詳情 Modal 裡本來就有一組一樣的 switch，這裡只是拉出來共用 */
const STATUS_LABEL = {
  pending: '等待確認',
  confirmed: '已確認',
  completed: '已完成',
  canceled: '已取消'
}

const AddButton = styled.div`
  position: absolute;
  top: 2px;
  left: -110px;
  width: 15px;
  height: 30px;
  padding: 0;
  /* font-size: 8px; */
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
  background-color: #ccc;
  border: none;
  z-index: 1;

  &:hover {
    background-color: #40a9ff;
  }
  @media (max-width: 820px) {
    left: -30px;
    width: 10px;
    height: 15px;
  }
`

/**
 * 月／週／日曆上的 hover 預覽卡。
 *
 * 為什麼不用 antd 的 Popover：FullCalendar 的事件是它自己畫的 DOM，
 * 要包 Popover 得把每一筆事件都包一層 React 元件，事件一多就變成幾百個
 * 監聽器。這裡改用 FullCalendar 自己的 eventMouseEnter/Leave，
 * 整張日曆只維護一張卡。
 *
 * pointer-events: none 是關鍵 —— 卡片絕對不能吃掉滑鼠事件，
 * 不然滑過去的瞬間會蓋住底下的預約，變成點不到、也會閃爍。
 */
const HoverCard = styled.div`
  position: fixed;
  z-index: 1200;
  width: 300px;
  max-height: 70vh;
  overflow: hidden;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e8e0e3;
  box-shadow: 0 8px 24px rgba(43, 34, 36, 0.16);
  font-size: 12.5px;
  line-height: 1.6;
  color: #2b2224;
  pointer-events: none;

  .hc-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f2eaed;
  }
  .hc-name {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 700;
    word-break: break-all;
  }
  .hc-status {
    flex-shrink: 0;
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
  }
  .hc-black {
    flex-shrink: 0;
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    background: #d4380d;
  }

  .hc-row {
    display: flex;
    gap: 8px;
    margin-top: 5px;
  }
  .hc-k {
    flex-shrink: 0;
    width: 62px;
    color: #8a7d80;
  }
  .hc-v {
    flex: 1;
    min-width: 0;
    word-break: break-all;
  }

  .hc-sep {
    margin: 9px 0 0;
    padding-top: 8px;
    border-top: 1px dashed #f2eaed;
  }

  .hc-note {
    margin-top: 4px;
    padding: 7px 9px;
    border-radius: 8px;
    background: #fbf7f8;
    color: #6b5c60;
    white-space: pre-wrap;
    word-break: break-all;
    /* 備註可能很長（客人病史、療程紀錄），最多五行，其餘點開詳情看 */
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .hc-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
  }
  .hc-tag {
    padding: 0 7px;
    border-radius: 999px;
    font-size: 11px;
    background: #f3eef0;
    color: #8a7d80;
  }

  .hc-hint {
    margin-top: 9px;
    font-size: 11px;
    color: #b0a4a7;
  }
`

const InfoItem = styled.div`
  display: flex;
  align-items: center;
`

const IconWrapper = styled.span`
  margin-right: 16px;
  font-size: 24px;
  color: #d5728a;
`

const StatusLegend = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  flex-wrap: wrap;
`

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  margin-right: 15px;
  font-size: 14px;
`

const StatusDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 5px;
  display: inline-block;
`

const CalendarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;

  .fc {
    height: 100%;
  }

  /* 保險：就算日曆內部又長出比畫面寬的東西，也把橫向捲動關在這一層，
     不要讓整個頁面跟著左右晃 */
  @media (max-width: 768px) {
    max-width: 100%;
    overflow-x: hidden;
  }
`

/**
 * 日曆本體的容器。
 *
 * 存在的理由只有一個：載入遮罩要 position: absolute 蓋在日曆上，
 * 需要一層 position: relative 的定位基準。高度規則照搬 CalendarWrapper
 * 的 flex 設定，插進來不會改變原本的版面。
 */
const CalendarSurface = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
`

/** 載入中的半透明遮罩。不吃滑鼠事件，只是讓店家知道資料還在路上。 */
const CalendarLoadingMask = styled.div`
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.72);
  pointer-events: none;
`

const CustomHeader = styled.div`
  margin-bottom: 10px;
`

/**
 * 日期跳轉列。
 *
 * FullCalendar 內建工具列只有上一頁／下一頁／今天，要看兩週後的某一天
 * 得按七次；日視圖更慘，一天一按。這一列讓店家直接挑日期。
 *
 * 視覺上刻意跟 .fc-button-primary（日曆自己的工具列按鈕）長一樣 ——
 * 白底、無框線、圓角 8px、淡陰影，hover 才轉成品牌粉。兩排控制項就在
 * 上下相鄰的位置，樣式不一致會顯得是外掛上去的。
 */
const DateJumpBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;

  .jump-label {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    color: #8c96a5;
    white-space: nowrap;
  }

  /* 日期選擇器：與日曆工具列同一套視覺 */
  .ant-picker {
    height: 34px;
    border: none;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    padding: 0 12px;
    transition: box-shadow 0.2s ease, background 0.2s ease;

    input {
      font-size: 14px;
      color: #555;
      font-weight: 500;
    }

    .ant-picker-suffix {
      color: #c9a9b4;
    }

    &:hover {
      background: #fdf5f8;
      box-shadow: 0 3px 8px rgba(213, 114, 138, 0.16);
    }

    &.ant-picker-focused {
      background: #f9edf2;
      box-shadow: 0 0 0 2px rgba(213, 114, 138, 0.16);

      input {
        color: #d5728a;
      }
    }
  }

  .today-btn {
    height: 34px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border: none;
    border-radius: 8px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 500;
    color: #666;
    background: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;

    &:hover {
      color: #d5728a;
      background: #f9edf2;
      box-shadow: 0 3px 8px rgba(213, 114, 138, 0.16);
    }

    &:active {
      transform: translateY(1px);
    }
  }

  @media (max-width: 768px) {
    gap: 6px;

    .jump-label {
      display: none; /* 手機版寬度有限，選擇器本身已經看得懂 */
    }

    .ant-picker {
      flex: 1;
      min-width: 0;
    }
  }
`

const StyledTag = styled(Tag)`
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 4px;
  border: none;
  color: ${(props) => props.textColor};
  background-color: ${(props) => props.backgroundColor};
`

const StylistInfo = styled.div`
  display: flex;
  align-items: center;
  background-color: #f0f2f5;
  padding: 5px;
  border-radius: 8px;
`

const StylistAvatar = styled(Avatar)`
  margin-right: 16px;
  width: 64px;
  height: 64px;
`

const StyledDrawer = styled(Drawer)`
  .ant-drawer-content-wrapper {
    width: 400px !important;
  }
  .ant-drawer-body {
    padding: 24px;
    background-color: #f8f9fa;
  }
  .ant-drawer-close {
    padding: 10px;
    background-color: #fcfcfc;
    border-radius: 5px;
    color: #666;
    font-size: 16px;
    transition: all 0.3s;
    &:hover {
      background-color: #f6f6f6;
      color: #000;
    }
  }
`

const MobileAddDrawer = styled(Drawer)`
  .ant-drawer-content-wrapper {
    width: 100% !important;
  }
  .ant-drawer-body {
    padding: 16px;
  }
`

const Appointments = () => {
  const navigate = useNavigate()
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [employees, setEmployees] = useState([])
  const [selectedEmployee, setSelectedEmployee] = useState('all')
  const [isAddModalVisible, setIsAddModalVisible] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [form] = Form.useForm()
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const { user, businessId } = useAuth()
  /**
   * 這個畫面上所有的時間都以「店家時區」為準，不看瀏覽器所在的時區 ——
   * 店家人在清邁（UTC+7）開後台，看到的仍然是台灣時間的預約。
   * 值來自 Business.timezone，未來要開海外店家改資料就好，不用動程式。
   */
  const bizTz = user?.timezone || getBusinessTimezone()
  const { hasFeature } = useMembership() // 會員方案權限檢查
  const [products, setProducts] = useState([])
  const [stylists, setStylists] = useState([])
  const [events, setEvents] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const calendarRef = useRef(null)
  // 首頁預設檢視只在進站時套用一次
  const homeViewAppliedRef = useRef(false)
  const pendingHomeViewRef = useRef(null)
  /**
   * 日期跳轉列用的狀態。
   *
   * FullCalendar 內建的工具列只有「上一頁／下一頁／今天」，要看兩週後的
   * 某一天得按七次。這兩個值由 datesSet 回填，讓外面的日期選擇器知道
   * 現在停在哪個視圖、哪一天。
   */
  const [calendarView, setCalendarView] = useState('dayGridMonth')
  const [calendarDate, setCalendarDate] = useState(() => tzNow())
  const initialFetchDone = useRef(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [visible, setVisible] = useState(false)
  const [originalPrice, setOriginalPrice] = useState(0)
  const [isDrawerVisible, setIsDrawerVisible] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedServices, setSelectedServices] = useState([]) // 選中的多個服務
  // 服務價格編輯狀態（serviceId -> { basePrice, actualPrice }）
  const [servicePrices, setServicePrices] = useState({})
  // 服務數量狀態（serviceId -> quantity）
  const [serviceQuantities, setServiceQuantities] = useState({})
  // 自訂服務時長：不勾就依服務項目時長加總（原行為），
  // 勾了就用店家輸入的分鐘數（後端以 durationMode: 'manual' 判斷）
  const [useCustomDuration, setUseCustomDuration] = useState(false)
  const [customDuration, setCustomDuration] = useState(null)
  // 預約人數
  const [participantCount, setParticipantCount] = useState(1)
  // 單筆預約人數上限（店家設定，1–4，預設 4；=1 時隱藏人數輸入）
  const [maxParticipants, setMaxParticipants] = useState(4)
  // 店家是否啟用定金功能。未啟用時整個定金狀態欄位不顯示，
  // 直接沿用 initialValues 的 not_required（讀取失敗也一律視為未啟用）
  const [depositEnabled, setDepositEnabled] = useState(false)
  useEffect(() => {
    if (!businessId) return
    api
      .get(`/api/business_B/${businessId}/deposit-settings`)
      .then((res) => setDepositEnabled(res?.data?.data?.isEnabled === true))
      .catch(() => setDepositEnabled(false))
  }, [businessId])
  useEffect(() => {
    if (!businessId) return
    api
      .get(`/api/business_B/${businessId}/appointment-settings`)
      .then((res) => {
        const n = Number(res?.data?.data?.maxParticipantsPerBooking)
        if (Number.isFinite(n) && n >= 1) setMaxParticipants(Math.min(4, n))
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId])

  // 客戶搜索相關狀態
  const [customers, setCustomers] = useState([])
  const [showCustomerList, setShowCustomerList] = useState(false)
  const [isComposing, setIsComposing] = useState(false) // 中文輸入法組合狀態
  const [customerNameValue, setCustomerNameValue] = useState('') // 客戶姓名輸入值

  // 新增：休假相關狀態
  const [businessDaysOff, setBusinessDaysOff] = useState([]) // 店家休假
  const [employeeDaysOff, setEmployeeDaysOff] = useState([]) // 選中員工休假（用於禁用時間）
  const [allEmployeeDaysOff, setAllEmployeeDaysOff] = useState([]) // 全部員工休假（用於顯示）
  const [selectedStylistId, setSelectedStylistId] = useState(null) // 選中的員工ID
  const [unavailableTimeSlots, setUnavailableTimeSlots] = useState([]) // 不可用的時間段
  const [isUpdateDateModalVisible, setIsUpdateDateModalVisible] =
    useState(false) // 改日期 Modal 顯示狀態
  const [updateDateForm] = Form.useForm() // 改日期表單
  const [updateDatePickerVisible, setUpdateDatePickerVisible] = useState(false) // 改日期日期選擇器顯示狀態

  // 班表：日曆上要顯示「誰排了哪個班」，資料來自月班表 API
  const [shiftInfoByDate, setShiftInfoByDate] = useState({}) // { 'YYYY-MM-DD': [row] }
  const [visibleMonths, setVisibleMonths] = useState([]) // ['YYYY-MM']
  /** 目前日曆可見的日期區間，給非 render 路徑（改完預約後重新整理）讀，避免 stale closure */
  const visibleRangeRef = useRef(null)
  // 目前畫面上看得到的日期區間（endKey 不含），用來決定時間軸要撐多開
  const [visibleRange, setVisibleRange] = useState(null)
  /**
   * 首頁時間軸設定：
   *   showFullDayTimeline = true  → 畫滿 00:00–24:00
   *   false（預設）               → 只畫 timelineRange（後端依營業時間算，前後各留一小時）
   */
  const [timelineSettings, setTimelineSettings] = useState({
    showFullDayTimeline: false,
    timelineRange: null
  })

  // 讀首頁時間軸設定（跟預約設定同一支 API，順便回傳依營業時間算好的範圍）
  useEffect(() => {
    if (!businessId) return
    let cancelled = false
    api
      .get(`/api/business_B/${businessId}/appointment-settings`)
      .then((res) => {
        if (cancelled) return
        const d = res.data?.data || {}
        setTimelineSettings({
          showFullDayTimeline: d.showFullDayTimeline === true,
          timelineRange: d.timelineRange || null
        })
        // 首頁預設檢視（店家在預約設定選的月／週／日）。
        // 設定是非同步拿到的，FullCalendar 已經用月檢視畫好了，這裡切一次即可；
        // 之後店家自己點月／週／日照常切換，不會被拉回來。
        const homeView = d.homeCalendarView
        if (
          ['timeGridWeek', 'timeGridDay'].includes(homeView) &&
          !homeViewAppliedRef.current
        ) {
          const calApi = calendarRef.current?.getApi()
          if (calApi) {
            homeViewAppliedRef.current = true
            calApi.changeView(homeView)
          } else {
            // 日曆還沒畫出來 → 等第一次 datesSet 再切
            pendingHomeViewRef.current = homeView
          }
        }
      })
      // 拿不到設定就用預設的 08:00–23:00，不要讓日曆開天窗
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [businessId])

  // 獲取店家休假
  const fetchBusinessDaysOff = async () => {
    try {
      const response = await api.get(`/api/business_B/${businessId}/dayoff`)
      // 響應格式：response.data.data
      setBusinessDaysOff(response.data.data || [])
    } catch (error) {
      console.error('獲取店家休假失敗:', error)
      setBusinessDaysOff([])
    }
  }

  // 獲取員工休假
  const fetchEmployeeDaysOff = async (employeeId) => {
    if (!employeeId) {
      setEmployeeDaysOff([])
      return
    }
    try {
      const response = await api.get(
        `/api/employees/${businessId}/${employeeId}/dayoff`
      )
      // 響應格式：response.data.data
      setEmployeeDaysOff(response.data.data || [])
    } catch (error) {
      console.error('獲取員工休假失敗:', error)
      setEmployeeDaysOff([])
    }
  }

  // 獲取所有員工休假（用於日曆顯示）
  const fetchAllEmployeesDaysOff = async (empList) => {
    if (!empList || empList.length === 0) {
      setAllEmployeeDaysOff([])
      return
    }
    try {
      const results = await Promise.all(
        empList.map((emp) =>
          api
            .get(`/api/employees/${businessId}/${emp._id}/dayoff`)
            .then((res) =>
              (res.data.data || []).map((item) => ({
                ...item,
                employeeId: emp._id,
                employeeName: emp.name
              }))
            )
            .catch(() => [])
        )
      )
      setAllEmployeeDaysOff(results.flat())
    } catch (error) {
      console.error('獲取所有員工休假失敗:', error)
      setAllEmployeeDaysOff([])
    }
  }

  /**
   * 取回可見範圍內的月班表，整理成「某天有哪幾個上班班別、各是誰」。
   *
   * 為什麼不重用休假那份資料：休假只記得「誰不在」，班表才知道「誰上早班」。
   * 上班的班別不會產生 dayOff，所以非得另外打這支 API 不可。
   *
   * 沒有班表檢視權限的角色會收到 403 —— 那就當作沒有班表資料，
   * 日曆照常顯示預約，不要整頁壞掉。
   */
  useEffect(() => {
    if (!businessId || visibleMonths.length === 0) {
      setShiftInfoByDate({})
      return
    }
    let cancelled = false

    const load = async () => {
      const payloads = await Promise.all(
        visibleMonths.map((month) =>
          api
            .get(`/api/businesses/${businessId}/schedule`, { params: { month } })
            .then((res) => res.data?.data || null)
            .catch(() => null)
        )
      )
      if (cancelled) return

      const map = {}
      payloads.filter(Boolean).forEach((payload) => {
        const nameById = new Map(
          (payload.employees || []).map((e) => [String(e.id), e.name])
        )
        /* 每一格存的是排班當下的顏色與名稱快照，店家後來改了班別設定，
           舊格子還留著舊顏色（同一個「早六」會忽藍忽綠）。
           顯示一律跟著現行班別設定走，跟班表頁同一個原則。 */
        const typeByCode = new Map(
          (payload.shiftTypes || []).map((t) => [String(t.code), t])
        )
        Object.entries(payload.cells || {}).forEach(([employeeId, byDate]) => {
          Object.entries(byDate || {}).forEach(([date, cell]) => {
            // 只要上班的班別；休假的格子已經走休假標籤那條路了
            if (!cell || cell.kind !== 'shift') return
            const code = String(cell.code || cell.label || '').trim()
            if (!code) return
            if (!map[date]) map[date] = []
            // 同一個班別的人合併成一行，不然五個人就是五行
            let row = map[date].find((r) => r.code === code)
            if (!row) {
              const type = typeByCode.get(code)
              row = {
                code,
                label: type?.label || cell.label || code,
                color: type?.color || cell.color || '',
                // 時間相反：要顯示那天實際排的時間，不是現在的設定
                start: cell.start || '',
                end: cell.end || '',
                names: []
              }
              map[date].push(row)
            }
            const who = nameById.get(String(employeeId)) || '員工'
            if (!row.names.includes(who)) row.names.push(who)
          })
        })
      })
      // 早班排前面；沒設時間的沉到最後
      Object.values(map).forEach((rows) =>
        rows.sort(
          (a, b) =>
            String(a.start || '99:99').localeCompare(String(b.start || '99:99')) ||
            String(a.code).localeCompare(String(b.code), 'zh-Hant')
        )
      )
      setShiftInfoByDate(map)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [businessId, visibleMonths])

  // 計算不可用的時間段（結合店家休假和員工休假）
  useEffect(() => {
    if (!selectedStylistId) {
      setUnavailableTimeSlots([])
      return
    }

    const unavailable = []
    const allDaysOff = [...businessDaysOff, ...employeeDaysOff]

    allDaysOff.forEach((dayOff) => {
      const start = dayjs(dayOff.start).tz(bizTz)
      const end = dayjs(dayOff.end).tz(bizTz)

      let current = start.clone()
      while (current.isBefore(end) || current.isSame(end, 'minute')) {
        unavailable.push(current.format('YYYY-MM-DD HH:mm'))
        current = current.add(15, 'minute')
      }
    })

    setUnavailableTimeSlots(unavailable)
  }, [selectedStylistId, businessDaysOff, employeeDaysOff])

  // 當選擇員工時，獲取該員工的休假（保留已選日期，不清空）
  const handleStylistChange = (stylistId) => {
    setSelectedStylistId(stylistId)
    form.setFieldsValue({ stylistId })
    fetchEmployeeDaysOff(stylistId)
  }

  // 禁用不可用的日期時間
  const disabledDateTime = (current) => {
    if (!current) return {}

    const currentStr = current.format('YYYY-MM-DD HH:mm')

    const isUnavailable = unavailableTimeSlots.some(
      (slot) => slot === currentStr
    )

    if (isUnavailable) {
      return {
        disabledHours: () => [current.hour()],
        disabledMinutes: () => [current.minute()]
      }
    }

    return {}
  }

  /**
   * 班別自訂顏色直接當文字色常常太淡（例如 #96d35f 的淺綠），
   * 所以底色用同色淡淡一層、文字用同色壓深，兩者都從店家設定的顏色推出來。
   */
  const parseHex = (hex) => {
    const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim())
    if (!m) return null
    let v = m[1]
    if (v.length === 3) v = v.split('').map((c) => c + c).join('')
    return [0, 2, 4].map((i) => parseInt(v.slice(i, i + 2), 16))
  }
  const mixWith = (hex, target, ratio) => {
    const rgb = parseHex(hex)
    if (!rgb) return null
    const out = rgb.map((c) => Math.round(c + (target - c) * ratio))
    return `rgb(${out.join(', ')})`
  }
  /** 標籤底色：往白色混到只剩一點顏色 */
  const shiftTintColor = (hex) => mixWith(hex, 255, 0.82) || '#f3f3f3'
  /** 標籤文字色：往黑色壓深，淺色班別也讀得到 */
  const shiftTextColor = (hex) => mixWith(hex, 0, 0.45) || '#555555'

  /**
   * 休假紀錄的 name 有兩種來源，只有班表那種能當「班別名稱」用：
   *   source='schedule' → 班表排的假別名稱（特休、例休、事假…），有意義
   *   source='manual'   → 休假頁自己打的字，實際資料裡有「整日休假」「休假時段」
   *                       這種通用字樣，也有「5/22」「已滿」這種隨手備註，
   *                       寫進標籤只會變成「Irene 5/22 整日」，所以一律顯示「休假」
   * 班表的假別若本來就叫「休假」，那也只是回到原本的顯示。
   */
  const dayOffTypeLabel = (dayOff) => {
    if (dayOff?.source !== 'schedule') return '休假'
    const text = String(dayOff?.name || '').trim()
    return text || '休假'
  }

  // 依日期聚合休假資訊（店休 + 全部員工休假）
  const dayOffInfoByDate = useMemo(() => {
    const map = {}

    const addRange = (startStr, endStr, updater) => {
      if (!startStr || !endStr) return
      let current = dayjs(startStr).tz(bizTz).startOf('day')
      const end = dayjs(endStr).tz(bizTz).endOf('day')
      while (current.isBefore(end) || current.isSame(end, 'day')) {
        const key = current.format('YYYY-MM-DD')
        if (!map[key]) {
          map[key] = {
            business: false,
            employees: [],
            businessSegments: [],
            employeeSegments: []
          }
        }
        updater(map[key])
        current = current.add(1, 'day')
      }
    }

    businessDaysOff.forEach((dayOff) => {
      addRange(dayOff.start, dayOff.end, (info) => {
        info.business = true
        const segKey = `${dayOff.start}~${dayOff.end}`
        if (!info.businessSegments.some((s) => s.key === segKey)) {
          info.businessSegments.push({
            key: segKey,
            start: dayOff.start,
            end: dayOff.end
          })
        }
      })
    })

    allEmployeeDaysOff.forEach((dayOff) => {
      const employeeName = dayOff.employeeName || '員工'
      addRange(dayOff.start, dayOff.end, (info) => {
        if (!info.employees.includes(employeeName)) {
          info.employees.push(employeeName)
        }
        const segKey = `${employeeName}~${dayOff.start}~${dayOff.end}`
        if (!info.employeeSegments.some((s) => s.key === segKey)) {
          info.employeeSegments.push({
            key: segKey,
            name: employeeName,
            // 班表排的假別名稱（特休／例休…），通用字樣會被正規化成「休假」
            typeName: dayOffTypeLabel(dayOff),
            employeeId: dayOff.employeeId,
            start: dayOff.start,
            end: dayOff.end
          })
        }
      })
    })

    return map
  }, [businessDaysOff, allEmployeeDaysOff, bizTz])

  // 休假詳情彈窗狀態（點擊日曆上的休假標籤後顯示具體時間區間）
  const [dayOffDetail, setDayOffDetail] = useState(null) // { dateKey, info }

  /**
   * 滑過預約時的預覽卡。
   * { event, x, y } —— x/y 是滑鼠座標，位置在 render 時再依視窗邊界翻面。
   * 只在桌機啟用：觸控裝置沒有 hover，點一下就直接開詳情 Modal 了。
   */
  const [hoverPreview, setHoverPreview] = useState(null)
  const hoverTimerRef = useRef(null)

  // 格式化單一休假時段為易讀的時間區間字串
  const formatDayOffSegment = (start, end) => {
    const s = dayjs(start).tz(bizTz)
    const e = dayjs(end).tz(bizTz)
    if (!s.isValid() || !e.isValid()) return ''
    if (s.isSame(e, 'day')) {
      return `${s.format('YYYY-MM-DD HH:mm')} ~ ${e.format('HH:mm')}`
    }
    return `${s.format('YYYY-MM-DD HH:mm')} ~ ${e.format('YYYY-MM-DD HH:mm')}`
  }

  /**
   * 把「某個休假區間」在「某一天」的實際涵蓋時段濃縮成日曆標籤用的短字串。
   *
   * 休假可能跨日，所以同一段區間在不同日期要顯示不同文字：
   *   整日涵蓋            → 整日
   *   當天開始、當天結束  → 13:00-18:00
   *   前一天就開始        → 至 18:00
   *   延續到隔天          → 13:00 起
   */
  const formatDayOffTimeForDate = (segment, dateKey) => {
    const s = dayjs(segment.start).tz(bizTz)
    const e = dayjs(segment.end).tz(bizTz)
    if (!s.isValid() || !e.isValid()) return ''

    const dayStart = dayjs.tz(dateKey, bizTz).startOf('day')
    const dayEnd = dayjs.tz(dateKey, bizTz).endOf('day')

    const startsBefore = s.isBefore(dayStart)
    const endsAfter = e.isAfter(dayEnd)

    // 這一天是不是從頭休到尾。
    // 跨日的整段休假（例如 9/15 00:00 ~ 9/19 23:59 的區間店休）在頭尾兩天
    // 只有單邊超出，不能只看 startsBefore && endsAfter，
    // 否則第一天會變成「00:00 起」、最後一天變成「至 23:59」。
    const fromDayStart = startsBefore || !s.isAfter(dayStart)
    // 允許幾分鐘誤差：00:00~23:59 也算整日
    const toDayEnd = endsAfter || e.isAfter(dayEnd.subtract(2, 'minute'))
    if (fromDayStart && toDayEnd) return '整日'

    if (startsBefore) return `至 ${e.format('HH:mm')}`
    if (endsAfter) return `${s.format('HH:mm')} 起`
    return `${s.format('HH:mm')}-${e.format('HH:mm')}`
  }

  /** 把同一位員工當天的多個休假區間合併成一行文字（例如「09:00-12:00、14:00-18:00」） */
  const buildDayOffTimeText = (segments, dateKey) => {
    const parts = segments
      .map((seg) => formatDayOffTimeForDate(seg, dateKey))
      .filter(Boolean)
    // 只要有任何一段是整日，整天就是休的，不必再列其他區間
    if (parts.includes('整日')) return '整日'
    return [...new Set(parts)].join('、')
  }

  /**
   * 這一天的標籤要寫哪個假別名稱。
   * 一天可能同時有兩種假別（很少見但資料允許），就用頓號串起來。
   * 先只看「整日」的那幾段 —— 標籤本來就只給整日休假用。
   */
  const buildDayOffTypeText = (segments, dateKey) => {
    const allDayNames = segments
      .filter((seg) => formatDayOffTimeForDate(seg, dateKey) === '整日')
      .map((seg) => seg.typeName)
      .filter(Boolean)
    const pool = allDayNames.length
      ? allDayNames
      : segments.map((seg) => seg.typeName).filter(Boolean)
    const uniq = [...new Set(pool)]
    return uniq.length ? uniq.join('、') : '休假'
  }

  /**
   * 有時段的休假 → 轉成日曆事件，才能跟預約一起按時間排序。
   * 整日休假不進來（沒有對應的時間點），維持在日期格右上角的標籤。
   * 跨日的休假會依日期切成每天一筆，各自帶當天的實際起訖。
   */
  const dayOffEvents = useMemo(() => {
    const result = []

    const pushPerDay = (startStr, endStr, meta) => {
      if (!startStr || !endStr) return
      const rangeStart = dayjs(startStr).tz(bizTz)
      const rangeEnd = dayjs(endStr).tz(bizTz)
      if (!rangeStart.isValid() || !rangeEnd.isValid()) return
      if (!rangeEnd.isAfter(rangeStart)) return

      let cursor = rangeStart.startOf('day')
      // 保險絲：避免壞資料造成無窮迴圈（最多展開一年）
      let guard = 0
      while (cursor.isBefore(rangeEnd) && guard < 366) {
        guard += 1
        const dateKey = cursor.format('YYYY-MM-DD')
        const timeText = formatDayOffTimeForDate(
          { start: startStr, end: endStr },
          dateKey
        )

        // 整日的留給右上角標籤，不做成事件
        if (timeText && timeText !== '整日') {
          const dayStart = cursor.startOf('day')
          const dayEnd = cursor.endOf('day')
          const segStart = rangeStart.isAfter(dayStart) ? rangeStart : dayStart
          const segEnd = rangeEnd.isBefore(dayEnd) ? rangeEnd : dayEnd

          result.push({
            id: `dayoff-${meta.key}-${dateKey}`,
            title: meta.isBusiness
              ? meta.name
              : `${meta.name} ${meta.typeName || '休假'}`,
            start: segStart.toISOString(),
            end: segEnd.toISOString(),
            backgroundColor: meta.isBusiness ? '#fde8ed' : '#f0e8ff',
            textColor: meta.isBusiness ? '#b65066' : '#6b4fa3',
            extendedProps: {
              type: 'dayoff',
              dayOffName: meta.name,
              isBusiness: !!meta.isBusiness,
              employeeId: meta.employeeId,
              dateKey,
              // FullCalendar 月檢視的 timeText 只給開始時間，
              // 休假要看到「幾點到幾點」才有意義，所以自己算好完整區間帶著走
              timeText
            }
          })
        }
        cursor = cursor.add(1, 'day')
      }
    }

    businessDaysOff.forEach((d, i) => {
      pushPerDay(d.start, d.end, {
        key: `biz-${i}`,
        name: '店休',
        isBusiness: true
      })
    })
    allEmployeeDaysOff.forEach((d, i) => {
      pushPerDay(d.start, d.end, {
        key: `emp-${d.employeeId || i}-${i}`,
        name: d.employeeName || '員工',
        // 有時段的假別（例如排了 13:00-18:00 的特休）也寫出名稱
        typeName: dayOffTypeLabel(d),
        employeeId: d.employeeId
      })
    })

    return result
  }, [businessDaysOff, allEmployeeDaysOff, bizTz])

  // 依「員工篩選」過濾休假事件（店休不受篩選影響，全店都適用）
  const visibleDayOffEvents = useMemo(() => {
    if (selectedEmployee === 'all') return dayOffEvents
    return dayOffEvents.filter(
      (ev) =>
        ev.extendedProps.isBusiness ||
        String(ev.extendedProps.employeeId) === String(selectedEmployee)
    )
  }, [dayOffEvents, selectedEmployee, bizTz])

  /**
   * 週／日檢視的時間軸要從幾點畫到幾點。
   *
   * 預設不畫滿 24 小時：十點開門七點關門的店，時間軸從 00:00 起跳等於每次
   * 進來都要先捲過十格空白才看得到第一筆預約。底線是後端依營業時間算好的
   * 範圍（前後各留一小時），店家可在「預約設定 → 首頁顯示完整 24 小時」改回全天。
   *
   * 但收合絕不能把單子藏起來 —— FullCalendar 的 slotMinTime/slotMaxTime 是直接
   * 把那幾列砍掉，落在外面的預約卡片會整張消失。所以這裡再往外撐，直到蓋住
   * 目前可見區間內實際存在的預約、休假與排班為止。
   */
  const timelineBounds = useMemo(() => {
    const DEFAULT_RANGE = { start: '08:00', end: '23:00' }
    if (timelineSettings.showFullDayTimeline) {
      return { min: '00:00:00', max: '24:00:00' }
    }

    // 'HH:mm' → 幾分鐘；'24:00' 也要收（代表一天的結尾）
    const toMinutes = (hhmm) => {
      const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim())
      if (!m) return null
      const h = Number(m[1])
      const min = Number(m[2])
      if (h > 24 || min > 59 || h * 60 + min > 1440) return null
      return h * 60 + min
    }

    const base = timelineSettings.timelineRange || DEFAULT_RANGE
    let minM = toMinutes(base.start) ?? toMinutes(DEFAULT_RANGE.start)
    let maxM = toMinutes(base.end) ?? toMinutes(DEFAULT_RANGE.end)
    if (minM >= maxM) {
      minM = toMinutes(DEFAULT_RANGE.start)
      maxM = toMinutes(DEFAULT_RANGE.end)
    }

    const inRange = (dateKey) =>
      !visibleRange ||
      (dateKey >= visibleRange.startKey && dateKey < visibleRange.endKey)

    const cover = (startMin, endMin) => {
      if (startMin === null || endMin === null) return
      if (startMin < minM) minM = startMin
      if (endMin > maxM) maxM = endMin
    }

    // 預約與休假事件：用台北時間的時分；跨午夜的那一筆在當天撐到 24:00
    const coverEvent = (ev) => {
      const s = dayjs(ev.start).tz(bizTz)
      const e = dayjs(ev.end || ev.start).tz(bizTz)
      if (!s.isValid() || !e.isValid()) return
      if (!inRange(s.format('YYYY-MM-DD'))) return
      const startMin = s.hour() * 60 + s.minute()
      const endMin = e.isSame(s, 'day') ? e.hour() * 60 + e.minute() : 1440
      cover(startMin, Math.max(startMin, endMin))
    }
    events.forEach(coverEvent)
    dayOffEvents.forEach(coverEvent)

    // 排班：班別時間也要看得到，不然排到 21:00 的晚班畫面上只畫到 20:00
    Object.entries(shiftInfoByDate).forEach(([dateKey, rows]) => {
      if (!inRange(dateKey)) return
      rows.forEach((row) => cover(toMinutes(row.start), toMinutes(row.end)))
    })

    // 往整點對齊，時間標籤才不會變成 09:30、10:30
    const startHour = Math.max(0, Math.floor(minM / 60))
    const endHour = Math.min(24, Math.ceil(maxM / 60))
    const pad = (n) => String(n).padStart(2, '0')
    return {
      min: `${pad(startHour)}:00:00`,
      max: endHour >= 24 ? '24:00:00' : `${pad(endHour)}:00:00`
    }
  }, [timelineSettings, visibleRange, events, dayOffEvents, shiftInfoByDate, bizTz])

  /* 捲動起點不能落在時間軸外面（例如只開下午的店，時間軸從 13:00 起跳，
     還丟 09:00 進去 FullCalendar 會直接捲到最上面，看起來像沒作用） */
  const calendarScrollTime =
    timelineBounds.min > '09:00:00' ? timelineBounds.min : '09:00:00'

  // 點擊日曆上的休假標籤
  const handleDayOffTagClick = (e, dateKey, info) => {
    e.stopPropagation()
    setDayOffDetail({ dateKey, info })
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
      setIsMobile(window.innerWidth <= 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  /**
   * 抓預約資料。
   *
   * 參數是日曆的可見區間 { from, to }（含頭含尾的 'YYYY-MM-DD'）；
   * 為了相容其他呼叫端，也接受單一 dayjs（會換算成它所在的整個月）。
   *
   * 為什麼是「區間」而不是「月」：
   *   日曆的可見範圍常常跨月 —— 日檢視的月底、週檢視跨月的那一週、
   *   月檢視頭尾補進來的格子。原本前端得自己挑一個月去抓，而 setEvents
   *   是整批覆蓋，挑錯就整片空白。店家回報的「9/30 預約消失」就是這樣：
   *   中間日被算成 10/1，於是抓了 10 月，9/30 根本不在資料裡。
   *   直接抓可見區間就不可能挑錯，而且比整個月更省 —— 週檢視只要 7 天。
   *
   * 這裡刻意不做快取：改完預約後的重新整理必須真的重打，
   * 進場時重複打同一支的問題由 utils/requestDedupe 的 in-flight 去重處理。
   */
  const fetchBookings = useCallback((input) => {
    const range =
      input && input.from && input.to
        ? input
        : {
            from: inTz(input).startOf('month').format('YYYY-MM-DD'),
            to: inTz(input).endOf('month').format('YYYY-MM-DD')
          }

    setIsLoading(true)
    api
      .get(`/api/business/bookings/month`, {
        params: { from: range.from, to: range.to }
      })
      .then((response) => {
        const bookings = (response.data || []).map((booking) => {
          const displayName = getBookingDisplayName(booking)
          const duration = booking.duration || 60
          const start = `${booking.date}T${booking.time}`
          const startMoment = dayjs.tz(start, bizTz)
          const calculatedEnd = startMoment.add(duration, 'minutes')

          // 檢查是否跨日：如果結束時間的日期與開始時間的日期不同，則限制在當天結束
          const startDate = startMoment.format('YYYY-MM-DD')
          const endDate = calculatedEnd.format('YYYY-MM-DD')

          // 如果跨日，將結束時間設為當天的 23:59:59
          const end =
            startDate !== endDate
              ? startMoment.endOf('day').format()
              : calculatedEnd.format()

          // 構建服務顯示名稱（包含數量）
          let serviceDisplayName = displayName
          if (booking.services && Array.isArray(booking.services)) {
            const servicesWithQuantity = booking.services
              .filter((s) => s.category !== 'additional')
              .map((s) => {
                const quantity = s.quantity || 1
                return quantity > 1
                  ? `${s.serviceName || s.name} × ${quantity}`
                  : s.serviceName || s.name
              })
            if (servicesWithQuantity.length > 0) {
              serviceDisplayName = servicesWithQuantity.join('、')
            }
          }

          // 構建標題（包含預約人數）
          let eventTitle = `${serviceDisplayName} - ${booking.stylistName || '待指派'}`
          if (booking.participantCount && booking.participantCount > 1) {
            eventTitle = `${serviceDisplayName} - ${booking.stylistName || '待指派'} (${booking.participantCount}人)`
          }

          return {
            id: booking._id,
            title: eventTitle,
            start: start,
            end: end,
            backgroundColor: getStatusColor(booking.status),
            borderColor: getStatusColor(booking.status),
            textColor: '#000000',
            extendedProps: {
              userName: booking.userName,
              serviceName: displayName, // 服務項目名稱
              status: booking.status,
              stylistId: booking.stylistId,
              stylistName: booking.stylistName,
              bookingSource: booking.bookingSource,
              phone: booking.phone,
              email: booking.email,
              additionalServices: booking.additionalServices || [],
              additionalServicesPrice: booking.additionalServicesPrice,
              note: booking.note,
              // 價格相關
              originalPrice: booking.originalPrice,
              discountedPrice: booking.discountedPrice,
              // 結帳狀態（方案三）
              hasCheckedOut: booking.hasCheckedOut,
              orderId: booking.orderId,
              orderStatus: booking.orderStatus,
              // 新增：預約人數和服務數量
              participantCount: booking.participantCount || 1,
              services: booking.services || [],
              // hover 預覽用：原始日期時間與時長（event.start 是組合後的 Date，
              // 要顯示「預估工時 / 預計完成」還是拿原始值最準）
              bookingDate: booking.date,
              bookingTime: booking.time,
              duration: duration,
              customerBirthdate: booking.customerBirthdate || null,
              customerProfile: booking.customerProfile || null
            }
          }
        })

        setEvents(bookings)
      })
      .catch((error) => {
        // 靜靜地失敗 = 店家看到一片空日曆卻以為「今天沒預約」。
        // 保留畫面上原本的資料，並且明確告訴他這次沒載到。
        console.error('獲取預約時出錯:', error)
        message.error('預約資料載入失敗，請重新整理或稍後再試')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [bizTz])

  const handleDatesSet = useCallback(
    (dateInfo) => {
      if (pendingHomeViewRef.current && !homeViewAppliedRef.current) {
        const v = pendingHomeViewRef.current
        pendingHomeViewRef.current = null
        homeViewAppliedRef.current = true
        if (dateInfo.view?.type !== v) {
          // 換檢視會再觸發一次 datesSet，這次的區間直接略過
          setTimeout(() => calendarRef.current?.getApi()?.changeView(v), 0)
          return
        }
      }
      // ⚠️ dateInfo.start / end 是 FullCalendar 用 dateEnv.toDate() 轉出來的
      // 「真實瞬間」（core/internal-common.js:2150），不是牆上時間。
      // 用 dayjs() 讀 = 用瀏覽器時區讀：店家人在 UTC+7 時整段可見區間往前一天，
      // 畫面上最後一天永遠抓不到預約（又是「9/30 預約消失」那個症狀）。
      const viewStart = inTz(dateInfo.start)
      const viewEnd = inTz(dateInfo.end)

      /**
       * 視圖的中間日。
       *
       * ⚠️ 不能寫成 viewStart.add(viewEnd.diff(viewStart, 'days') / 2, 'days')。
       * FullCalendar 的 end 是「排他」的，所以日檢視 diff=1、週檢視 diff=7，
       * 除以 2 會得到 0.5 / 3.5 —— 而 dayjs 的 add() 會把它 Math.round 成
       * 1 / 4，整個中間日往後偏：
       *   日檢視 9/30（9/30~10/1）→ 被算成 10/1
       *   週檢視 9/27~10/3       → 被算成 10/1
       * 以前拿這個日期去決定「要抓哪個月的預約」，所以每個月的最後一天、
       * 以及跨月的那一週，預約都會整片消失。改用毫秒中點就不會有小數問題。
       */
      const middleDate = dayjs(
        viewStart.valueOf() + (viewEnd.valueOf() - viewStart.valueOf()) / 2
      )

      // 同步給日期跳轉列。日／週視圖用起始日（就是使用者看的那一天／那一週），
      // 月視圖用中間日 —— 月視圖的 start 是上個月補進來的那幾格，拿它當
      // 「現在是哪個月」會整個差一個月。
      setCalendarView(dateInfo.view?.type || 'dayGridMonth')
      setCalendarDate(
        dateInfo.view?.type === 'dayGridMonth' ? middleDate : viewStart
      )

      // 班表是一次一個月，可見範圍跨月（月視圖頭尾補格、跨月的那一週）就抓兩個月。
      // 用字串陣列比對，同一個月來回切檢視不會重打 API。
      const months = []
      let cursor = viewStart.startOf('month')
      const lastMonth = viewEnd.subtract(1, 'day').startOf('month')
      while (cursor.isBefore(lastMonth) || cursor.isSame(lastMonth, 'month')) {
        months.push(cursor.format('YYYY-MM'))
        cursor = cursor.add(1, 'month')
      }
      setVisibleMonths((prev) =>
        prev.length === months.length && prev.every((m, i) => m === months[i])
          ? prev
          : months
      )

      const startKey = viewStart.format('YYYY-MM-DD')
      const endKey = viewEnd.format('YYYY-MM-DD')
      setVisibleRange((prev) =>
        prev && prev.startKey === startKey && prev.endKey === endKey
          ? prev
          : { startKey, endKey }
      )

      // 預約直接照可見區間抓。endKey 是 FullCalendar 的排他結束日，
      // 要退一天才是畫面上最後一格
      const bookingRange = {
        from: startKey,
        to: viewEnd.subtract(1, 'day').format('YYYY-MM-DD')
      }
      visibleRangeRef.current = bookingRange
      fetchBookings(bookingRange)
    },
    [fetchBookings]
  )

  /** 日期選擇器選了某天 → 讓日曆跳過去 */
  const handleJumpToDate = useCallback((value) => {
    if (!value) return
    calendarRef.current?.getApi()?.gotoDate(value.toDate())
  }, [])

  /**
   * 切換店家時重抓預約。
   *
   * ⚠️ 第一次拿到 businessId 時不要抓。
   * FullCalendar 掛載時的 datesSet 已經照可見區間抓過同一份資料了
   * （子元件的 componentDidMount 比父層的 useEffect 早跑），這裡再抓一次等於
   *   打兩趟同一支 API → setEvents 兩次 → 日曆整批重畫兩次，
   * 就是進場時「閃一下才出現預約」的主因。
   * 而且兩次的區間算法不同（這裡沒有 visibleRange 時退回「整個月」，
   * datesSet 給的是可見區間），誰後回來誰覆蓋，月初月底補進來的格子
   * 有機會整片消失 —— 也就是店家說的「看不到預約」。
   */
  const lastBusinessIdRef = useRef(null)
  useEffect(() => {
    if (!businessId) return
    const previousBusinessId = lastBusinessIdRef.current
    lastBusinessIdRef.current = businessId
    if (previousBusinessId === null || previousBusinessId === businessId) return
    if (!calendarRef.current) return
    const calendarApi = calendarRef.current.getApi()
    const currentDate = inTz(calendarApi.getDate())
    fetchBookings(visibleRangeRef.current || currentDate)
  }, [businessId, fetchBookings])

  useEffect(() => {
    if (!initialFetchDone.current && businessId) {
      // 獲取員工數據
      api
        .get(`/api/employees/${businessId}`)
        .then((response) => {
          setEmployees(response.data)
          setStylists(response.data)
          fetchAllEmployeesDaysOff(response.data)
        })
        .catch((error) => {
          console.error('獲取員工時出錯:', error)
        })

      // 獲取家的所有服務
      api
        .get(`/api/business_B/${businessId}/products`)
        .then((response) => {
          setProducts(response.data)
        })
        .catch((error) => console.error('獲取服務時出錯:', error))

      // 獲取店家休假
      fetchBusinessDaysOff()

      initialFetchDone.current = true
    }
  }, [businessId])

  const buttonText = {
    today: '今天',
    month: '月',
    week: '週',
    day: '日',
    list: '列表'
  }

  const handleEventClick = (info) => {
    // 休假事件沒有預約資料，開預約抽屜會整片空白 —— 改開休假詳情彈窗
    if (info.event.extendedProps?.type === 'dayoff') {
      const dateKey = info.event.extendedProps.dateKey
      const dayInfo = dayOffInfoByDate[dateKey]
      if (dayInfo) setDayOffDetail({ dateKey, info: dayInfo })
      return
    }
    setSelectedEvent(info.event)
    setIsDrawerVisible(true)
  }

  const handleCloseDrawer = () => {
    setIsDrawerVisible(false)
    setSelectedEvent(null)
  }

  // 從預約數據中直接讀取結帳狀態（方案三）
  const getOrderInfo = () => {
    if (!selectedEvent) return { hasOrder: false, orderInfo: null }
    const hasCheckedOut = selectedEvent.extendedProps?.hasCheckedOut === true
    const orderId = selectedEvent.extendedProps?.orderId
    return {
      hasOrder: hasCheckedOut,
      orderInfo: orderId
        ? {
            orderId: orderId,
            status: selectedEvent.extendedProps?.orderStatus || 'paid'
          }
        : null
    }
  }

  // 取消訂單交易
  const handleCancelOrder = async (orderId, bookingId) => {
    try {
      await api.post(
        `/api/businesses/${businessId}/checkout/orders/${orderId}/void`,
        {
          reason: '重新結帳',
          bookingId: bookingId
        }
      )
      message.success('訂單已取消')
      // 重新獲取預約數據以更新結帳狀態
      const calendarApi = calendarRef.current?.getApi()
      if (calendarApi) {
        const currentDate = inTz(calendarApi.getDate())
        // 重新整理要照目前看到的區間抓，只抓一個月會把跨月那半週洗成空的
        fetchBookings(visibleRangeRef.current || currentDate)
      }
    } catch (error) {
      console.error('取消訂單失敗:', error)
      message.error('取消訂單失敗')
      throw error
    }
  }

  // 處理結帳
  const handleCheckout = () => {
    if (!selectedEvent) return
    const { hasOrder } = getOrderInfo()
    if (hasOrder) {
      message.warning('此預約已結帳，請使用「取消交易並再次結帳」功能')
      return
    }
    const bookingId = selectedEvent.id
    navigate(`/checkout/checkout?bookingId=${bookingId}`)
    handleCloseDrawer()
  }

  // 重新結帳（優先傳遞 orderId，結帳頁會從訂單詳情載入完整內容）
  const handleRecheckout = async () => {
    if (!selectedEvent) return
    const { orderInfo } = getOrderInfo()
    if (!orderInfo?.orderId) return

    Modal.confirm({
      title: '重新結帳',
      content:
        '此操作將取消原訂單並重新結帳，原訂單的消費內容（服務、商品、客戶、付款方式等）將自動帶入結帳頁面。確定繼續嗎？',
      okText: '確認',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await handleCancelOrder(orderInfo.orderId, selectedEvent.id)
          const params = new URLSearchParams({ recheckout: 'true' })
          params.set('orderId', orderInfo.orderId)
          params.set('bookingId', selectedEvent.id)
          navigate(`/checkout/checkout?${params.toString()}`)
          handleCloseDrawer()
        } catch (error) {
          message.error('操作失敗')
        }
      }
    })
  }

  // 僅取消交易
  const handleCancelOnly = async () => {
    if (!selectedEvent) return
    const { orderInfo } = getOrderInfo()
    if (!orderInfo?.orderId) return

    Modal.confirm({
      title: '確認取消交易',
      content: `確定要取消訂單 ${orderInfo.orderId} 的交易嗎？此操作將作廢該訂單。`,
      okText: '確認',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await handleCancelOrder(orderInfo.orderId, selectedEvent.id)
        } catch (error) {
          // 錯誤已在 handleCancelOrder 中處理
        }
      }
    })
  }

  // 處理改日期
  const handleUpdateDate = () => {
    if (!selectedEvent) return
    const { orderInfo } = getOrderInfo()
    if (!orderInfo?.orderId) return

    // 設置表單初始值為當前日期和時間
    const currentDate = dayjs(selectedEvent.start).tz(bizTz)
    const currentEnd = dayjs(selectedEvent.end).tz(bizTz)

    updateDateForm.setFieldsValue({
      date: currentDate,
      startTime: currentDate.format('HH:mm'),
      endTime: currentEnd.format('HH:mm')
    })

    setIsUpdateDateModalVisible(true)
  }

  // 提交改日期
  const handleUpdateDateSubmit = async () => {
    try {
      const values = await updateDateForm.validateFields()
      const { orderInfo } = getOrderInfo()
      if (!orderInfo?.orderId) return

      const formattedDate = values.date.format('YYYY-MM-DD')
      const startTime = values.startTime || values.date.format('HH:mm')
      const endTime = values.endTime

      const payload = {
        date: formattedDate,
        startTime: startTime
      }

      if (endTime) {
        payload.endTime = endTime
      }

      await api.patch(
        `/api/businesses/${businessId}/checkout/orders/${orderInfo.orderId}/date`,
        payload
      )

      message.success('訂單日期更新成功')
      setIsUpdateDateModalVisible(false)
      updateDateForm.resetFields()

      // 重新獲取預約數據以更新顯示
      const calendarApi = calendarRef.current?.getApi()
      if (calendarApi) {
        const currentDate = inTz(calendarApi.getDate())
        // 重新整理要照目前看到的區間抓，只抓一個月會把跨月那半週洗成空的
        fetchBookings(visibleRangeRef.current || currentDate)
      }
    } catch (error) {
      console.error('更新訂單日期失敗:', error)
      message.error(error.response?.data?.message || '更新訂單日期失敗')
    }
  }

  const handleEmployeeFilter = (employeeId) => {
    setSelectedEmployee(employeeId)
  }

  /**
   * 依員工篩選後的預約。
   *
   * ⚠️ 這裡刻意用 useMemo 而不是「useState + useEffect 同步」。
   * 原本的寫法會多一輪 render：events 更新的那一次 render，畫面上掛的還是
   * 上一批 filteredEvents，等 effect 跑完才換掉 —— 進場時就是先畫一次空日曆
   * 再補上預約，店家看到的就是「閃一下」。改成推導值後，events 一到就同一輪畫出來。
   */
  const filteredEvents = useMemo(() => {
    if (selectedEmployee === 'all') return events
    return events.filter(
      (event) => event.extendedProps.stylistId === selectedEmployee
    )
  }, [events, selectedEmployee])

  const handleDateClick = (info) => {
    // 點擊休假／店休標籤時，只顯示休假詳情彈窗，不要再跳出「新增預約」。
    // （FullCalendar 的 dateClick 綁在原生 DOM 上，標籤 onClick 的 stopPropagation
    //   無法阻止它，故在此依點擊目標判斷並提前返回。）
    if (info?.jsEvent?.target?.closest?.('.day-off-tag')) {
      return
    }
    // info.date 一樣是真實瞬間；換成店家時區才會是使用者點到的那一格
    const clickedAt = inTz(info.date)
    setSelectedDate(clickedAt)
    form.setFieldsValue({ date: clickedAt })
    setUnavailableTimeSlots([]) // 重置不可用時間
    // 造型師預設帶入登入者本人（若在員工清單中）；非員工帳號（如老闆）維持空值
    const selfStylist = stylists.find(
      (s) => String(s._id) === String(user?.userId || '')
    )
    if (selfStylist) {
      setSelectedStylistId(selfStylist._id)
      form.setFieldsValue({ stylistId: selfStylist._id })
      fetchEmployeeDaysOff(selfStylist._id)
    } else {
      setSelectedStylistId(null) // 重置選中的員工
    }
    fetchBusinessDaysOff() // 獲取店家休假
    setIsAddModalVisible(true)
  }

  const renderDayCellContent = (args) => {
    // args.date 是真實瞬間；用瀏覽器時區讀會整格往前一天，
    // 於是休假／店休標籤全部畫錯格子（該休的那天反而空白）
    const dateKey = inTz(args.date).format('YYYY-MM-DD')
    const dayOffInfo = dayOffInfoByDate[dateKey]

    // 依員工分組，算出各自當天的休假時段
    const employeeDayOffRows = (dayOffInfo?.employees || []).map((name) => {
      const segs = (dayOffInfo.employeeSegments || []).filter(
        (seg) => seg.name === name
      )
      return {
        name,
        timeText: buildDayOffTimeText(segs, dateKey),
        // 班表排的假別寫出實際名稱（特休、例休…），沒有就退回「休假」
        typeText: buildDayOffTypeText(segs, dateKey)
      }
    })

    // 右上角標籤只放「整日休假」——有時段的已經以事件形式排進時間序，
    // 兩邊都顯示會變成同一筆休假出現兩次。
    const allDayRows = employeeDayOffRows.filter((r) => r.timeText === '整日')
    // 手機版格子窄，超過 2 人就收合成「其他 N 人休假」
    const MOBILE_TAG_LIMIT = 2
    const shouldCollapse = isMobile && allDayRows.length > MOBILE_TAG_LIMIT
    const visibleRows = shouldCollapse
      ? allDayRows.slice(0, MOBILE_TAG_LIMIT)
      : allDayRows
    const collapsedCount = allDayRows.length - visibleRows.length

    const businessTimeText = buildDayOffTimeText(
      dayOffInfo?.businessSegments || [],
      dateKey
    )
    const showBusinessTag = businessTimeText === '整日'

    // 當天排了哪幾個上班班別（一個班別一行，同班的人併在一起）。
    // 全店公休那天不列——沒人上班，列出來只是佔位置。
    const shiftRows = showBusinessTag ? [] : shiftInfoByDate[dateKey] || []
    const MOBILE_SHIFT_LIMIT = 2
    const visibleShiftRows =
      isMobile && shiftRows.length > MOBILE_SHIFT_LIMIT
        ? shiftRows.slice(0, MOBILE_SHIFT_LIMIT)
        : shiftRows
    const hiddenShiftCount = shiftRows.length - visibleShiftRows.length

    return (
      <>
        <AddButton
          onClick={(e) => {
            e.stopPropagation()
            handleDateClick(args)
          }}
        ></AddButton>
        {args.dayNumberText}
        {showBusinessTag && (
          <span
            className='day-off-tag clickable'
            title='點擊查看店休時間'
            onClick={(e) => handleDayOffTagClick(e, dateKey, dayOffInfo)}
          >
            店休 <span className='day-off-time'>整日</span>
          </span>
        )}
        {/* 上班班別：一個班別一行，例如「早三 Irene、Lynn」 */}
        {visibleShiftRows.map((row) => (
          <span
            key={`shift-${row.code}`}
            className='shift-tag'
            style={{
              borderLeftColor: row.color || '#c9c9c9',
              background: shiftTintColor(row.color),
              color: shiftTextColor(row.color)
            }}
            title={`${row.label}${
              row.start && row.end ? ` ${row.start}-${row.end}` : ''
            }｜${row.names.join('、')}`}
          >
            <span className='shift-code'>{row.code}</span>
            {/* 班別代號跟人名一樣時（例如專屬某人的「Emma」班）不重複寫兩次 */}
            {!(row.names.length === 1 && row.names[0] === row.code) && (
              <>
                {' '}
                <span className='shift-names'>{row.names.join('、')}</span>
              </>
            )}
          </span>
        ))}
        {hiddenShiftCount > 0 && (
          <span
            className='shift-tag'
            title={shiftRows
              .slice(MOBILE_SHIFT_LIMIT)
              .map((r) => `${r.label}：${r.names.join('、')}`)
              .join('\n')}
          >
            <span className='shift-names'>其他 {hiddenShiftCount} 個班別</span>
          </span>
        )}
        {/* 整日休假：每人一行；手機版超過 2 人收合 */}
        {visibleRows.map((row) => (
          <span
            key={row.name}
            className='day-off-tag employee clickable'
            title={`${row.name} ${row.typeText}（整日）｜點擊查看詳細時間`}
            onClick={(e) => handleDayOffTagClick(e, dateKey, dayOffInfo)}
          >
            {row.name} {row.typeText}{' '}
            <span className='day-off-time'>整日</span>
          </span>
        ))}
        {collapsedCount > 0 && (
          <span
            className='day-off-tag employee clickable'
            title={`${allDayRows
              .slice(MOBILE_TAG_LIMIT)
              .map((r) => r.name)
              .join('、')} 整日休假｜點擊查看詳細時間`}
            onClick={(e) => handleDayOffTagClick(e, dateKey, dayOffInfo)}
          >
            其他 {collapsedCount} 人休假
          </span>
        )}
      </>
    )
  }

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      const values = await form.validateFields()

      // 將日期和時間分開處理。
      // ⚠️ 這裡刻意「不」做時區換算：values.date 是使用者在選擇器上看到的牆上時間，
      // 他挑 9/15 14:00 就是要 9/15 14:00。原本先 .toDate() 再用 dayjs() 讀，
      // 等於轉成瀏覽器時區 —— 店家人在清邁點月曆的 9/15，存進 DB 會變成 9/14 23:00。
      const formattedDate = values.date.format('YYYY-MM-DD')
      const formattedTime = values.date.format('HH:mm')

      // 获取选中的多个服务和造型师的详细信息
      const selectedProductIds = values.productIds || []
      const selectedProducts = products.filter((product) =>
        selectedProductIds.some((id) => String(product._id) === String(id))
      )
      const selectedStylist = stylists.find(
        (stylist) => String(stylist._id) === String(values.stylistId)
      )

      // 構建 selectedServices 數組（包含 basePrice, actualPrice 和 quantity）
      const selectedServicesForSubmit = selectedProducts.map((product) => {
        const serviceId = product._id
        const basePrice = product.price || 0
        const priceInfo = servicePrices[serviceId]
        const actualPrice = priceInfo?.actualPrice ?? basePrice
        const quantity = serviceQuantities[serviceId] || 1

        return {
          serviceId,
          basePrice,
          actualPrice,
          quantity
        }
      })

      // 計算原價（使用 basePrice 總和 * 數量）
      const totalOriginalPrice = selectedServicesForSubmit.reduce(
        (sum, service) =>
          sum + (service.basePrice || 0) * (service.quantity || 1),
        0
      )
      // 計算優惠價（使用 actualPrice 總和 * 數量）
      const totalActualPrice = selectedServicesForSubmit.reduce(
        (sum, service) =>
          sum + (service.actualPrice || 0) * (service.quantity || 1),
        0
      )
      const discountedPrice = values.discountedPrice || totalActualPrice
      const totalDuration = selectedProducts.reduce(
        (sum, p) => sum + (p.duration || 0) * (serviceQuantities[p._id] || 1),
        0
      )

      // 構建服務數組（用於向後兼容和未來擴展）
      const servicesArray = selectedProducts.map((product) => {
        const serviceId = product._id
        const basePrice = product.price || 0
        const priceInfo = servicePrices[serviceId]
        const actualPrice = priceInfo?.actualPrice ?? basePrice
        const quantity = serviceQuantities[serviceId] || 1

        return {
          serviceId,
          serviceName: product.name,
          basePrice,
          actualPrice,
          duration: product.duration,
          category: product.category || null,
          quantity
        }
      })

      // 主要服務名稱（用於顯示，選擇第一個服務）
      const mainProductName =
        selectedProducts.length > 0
          ? selectedProducts.map((p) => p.name).join('、')
          : ''

      const newAppointment = {
        ...values,
        businessId,
        userId: user.id,
        status: 'confirmed',
        customerType: values.customerType || 'new',
        customerSource: values.customerSource || 'none',
        // 向後兼容：保留單個服務字段
        productId: selectedProductIds[0] || null,
        productName: mainProductName,
        // 新增：多個服務支持
        productIds: selectedProductIds,
        services: servicesArray,
        // 發送 selectedServices 數組用於價格更新（包含 serviceId, basePrice, actualPrice, quantity）
        selectedServices: selectedServicesForSubmit,
        stylistName: selectedStylist ? selectedStylist.name : '',
        // 服務時長：自訂時送店家輸入的分鐘數，後端才不會用服務加總覆蓋
        duration:
          useCustomDuration && customDuration
            ? Number(customDuration)
            : totalDuration,
        durationMode:
          useCustomDuration && customDuration ? 'manual' : 'auto',
        depositStatus: values.depositStatus || 'not_required',
        depositDueDate: null,
        date: formattedDate,
        time: formattedTime,
        originalPrice: totalOriginalPrice,
        discountedPrice: discountedPrice, // 確保有值
        // 預約人數
        participantCount: values.participantCount || participantCount || 1,
        notifyCustomer: values.notifyCustomer !== false
      }

      // 調用 API 保存新的預約
      const response = await api.post('/api/business/bookings', newAppointment)

      message.success('預約已成功添加')
      setIsAddModalVisible(false)
      form.resetFields()
      setCustomerNameValue('')
      setSelectedServices([])
      setServicePrices({}) // 重置服務價格狀態
      setServiceQuantities({}) // 重置服務數量狀態
      setUseCustomDuration(false) // 重置自訂時長
      setCustomDuration(null)
      setParticipantCount(1) // 重置預約人數

      // 直接添加新預約到狀態中
      const displayName = getBookingDisplayName({
        services: servicesArray,
        productName: newAppointment.productName
      })

      const newEvent = {
        id: response.data._id,
        title: `${displayName} - ${newAppointment.stylistName || '待指派'}`,
        start: dayjs
          .tz(
            `${formattedDate} ${formattedTime}`,
            'YYYY-MM-DD HH:mm',
            bizTz
          )
          .toDate(),
        end: dayjs
          .tz(
            `${formattedDate} ${formattedTime}`,
            'YYYY-MM-DD HH:mm',
            bizTz
          )
          .add(newAppointment.duration, 'minutes')
          .toDate(),
        extendedProps: {
          userName: newAppointment.userName,
          serviceName: displayName, // 服務項目名稱
          status: newAppointment.status,
          stylistId: newAppointment.stylistId,
          stylistName: newAppointment.stylistName,
          bookingSource: 'business'
        }
      }
      setEvents((prevEvents) => [...prevEvents, newEvent])
      // 原本這裡會 setKey 換掉 FullCalendar 的 key，把整個日曆砍掉重建 ——
      // 畫面會整片白一下再長回來。events 更新本來就會讓日曆重畫，
      // 改成重抓目前可見區間，拿回後端的權威資料即可。
      fetchBookings(visibleRangeRef.current || calendarDate)
    } catch (error) {
      console.error('添加預約時出錯:', error)
      message.error('添加預約失敗，請重試')
    } finally {
      setIsSubmitting(false)
    }
  }

  // 修改 handleAddModalOk 使用 handleSubmit
  const handleAddModalOk = () => {
    handleSubmit()
  }

  const handleAddModalCancel = () => {
    setIsAddModalVisible(false)
    form.resetFields()
    setCustomerNameValue('')
    setSelectedServices([])
    setServicePrices({}) // 重置服務價格狀態
    setServiceQuantities({}) // 重置服務數量狀態
    setUseCustomDuration(false) // 重置自訂時長
    setCustomDuration(null)
    setParticipantCount(1) // 重置預約人數
    setCustomers([])
    setShowCustomerList(false)
    setSelectedStylistId(null)
    setUnavailableTimeSlots([])
  }

  const titleFormat =
    windowWidth <= 390 ? { month: 'long' } : { year: 'numeric', month: 'long' }

  // 處理多選服務
  const handleServicesSelect = (serviceIds) => {
    const selectedProducts = products.filter((product) =>
      serviceIds.some((id) => String(product._id) === String(id))
    )
    setSelectedServices(selectedProducts)

    // 初始化服務價格和數量狀態
    const initialServicePrices = {}
    const initialServiceQuantities = {}
    selectedProducts.forEach((service) => {
      const serviceId = service._id
      const basePrice = service.price || 0
      initialServicePrices[serviceId] = {
        basePrice,
        actualPrice: basePrice // 默認實際價格等於原價
      }
      // 初始化服務數量為1
      initialServiceQuantities[serviceId] = serviceQuantities[serviceId] || 1
    })
    setServicePrices(initialServicePrices)
    setServiceQuantities((prev) => ({ ...prev, ...initialServiceQuantities }))

    // 計算原價（basePrice 總和 * 數量）和優惠價（actualPrice 總和 * 數量）
    const totalBasePrice = selectedProducts.reduce((sum, product) => {
      const quantity = initialServiceQuantities[product._id] || 1
      return sum + (product.price || 0) * quantity
    }, 0)
    const totalActualPrice = selectedProducts.reduce((sum, product) => {
      const quantity = initialServiceQuantities[product._id] || 1
      return sum + (product.price || 0) * quantity
    }, 0)
    const totalDuration = selectedProducts.reduce((sum, product) => {
      const quantity = initialServiceQuantities[product._id] || 1
      return sum + (product.duration || 0) * quantity
    }, 0)

    // 更新表單值（須寫入 productIds，程式帶入上一筆時才會顯示在多選框）
    form.setFieldsValue({
      productIds: serviceIds,
      originalPrice: totalBasePrice, // 原價 = basePrice 總和 * 數量
      discountedPrice: totalActualPrice // 優惠價 = actualPrice 總和 * 數量
    })
    setOriginalPrice(totalBasePrice)
  }

  // 保留舊的單選處理函數（向後兼容）
  const handleProductSelect = (value, option) => {
    const selectedProduct = products.find(
      (product) => String(product._id) === String(value)
    )
    const price = selectedProduct ? selectedProduct.price : 0

    form.setFieldsValue({
      productName: option.children,
      duration: selectedProduct ? selectedProduct.duration : null,
      originalPrice: price,
      discountedPrice: price // 自動設定優惠價等於原價
    })
    setOriginalPrice(price)
  }

  // 搜索客戶
  const searchCustomers = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setCustomers([])
      setShowCustomerList(false)
      return
    }

    try {
      const response = await api.get(
        `/api/business_B/${businessId}/customers/search`,
        {
          params: { searchTerm }
        }
      )
      if (response.data.success) {
        setCustomers(response.data.data)
        setShowCustomerList(response.data.data.length > 0)
      } else {
        setCustomers([])
        setShowCustomerList(false)
      }
    } catch (error) {
      console.error('搜索客戶失敗:', error)
      setCustomers([])
      setShowCustomerList(false)
    }
  }

  // 依 customerId 取得該客戶上一筆預約（用於選擇客戶後詢問是否帶入）
  const fetchLastBookingByCustomerId = async (customerId) => {
    if (!customerId) return null
    try {
      const res = await api.get(
        `/api/business/bookings/customer/${customerId}`,
        { params: { page: 1, limit: 50 } }
      )
      const raw = res?.data
      const list = Array.isArray(raw?.bookings)
        ? raw.bookings
        : Array.isArray(raw?.data?.bookings)
          ? raw.data.bookings
          : Array.isArray(raw)
            ? raw
            : []
      if (!list.length) return null
      const sorted = [...list].sort((a, b) => {
        const aVal = new Date(`${a.date} ${a.time || '00:00'}`).getTime()
        const bVal = new Date(`${b.date} ${b.time || '00:00'}`).getTime()
        return bVal - aVal
      })
      return sorted[0]
    } catch (e) {
      console.warn('取得客戶上一筆預約失敗', e)
      return null
    }
  }

  // 將上一筆預約帶入新增表單（與 BookingTable：支援僅 productId、ObjectId 字串比對、服務名後援）
  const applyLastBookingToForm = (lastBooking) => {
    if (!lastBooking) return
    try {
      // 自動帶入不覆寫日期／時間，保留使用者已在月曆或表單選好的值
      form.setFieldsValue({
        stylistId: lastBooking.stylistId || undefined,
        note: lastBooking.note || undefined
      })
      setSelectedStylistId(lastBooking.stylistId || null)

      const list = products || []
      if (!list.length) return

      const normName = (s) => String(s || '').replace(/\s+/g, '').trim()
      const findProduct = (rawId, serviceName) => {
        if (rawId != null && String(rawId).trim() !== '') {
          const byId = list.find((p) => String(p._id) === String(rawId))
          if (byId) return byId
        }
        const n = String(serviceName || '').trim()
        if (n) {
          const byExact = list.find((p) => (p.name || '').trim() === n)
          if (byExact) return byExact
          const nn = normName(n)
          if (nn) {
            const byNorm = list.find((p) => normName(p.name) === nn)
            if (byNorm) return byNorm
          }
        }
        return null
      }

      const resolveProductIds = () => {
        const ids = []
        if (lastBooking.services?.length) {
          for (const s of lastBooking.services) {
            const raw = s.serviceId ?? s.productId
            const p = findProduct(raw, s.serviceName)
            if (p) ids.push(p._id)
          }
        }
        if (ids.length === 0 && lastBooking.productId) {
          const p = findProduct(
            lastBooking.productId,
            lastBooking.productName
          )
          if (p) ids.push(p._id)
        }
        return ids
      }

      const productIds = resolveProductIds()
      if (productIds.length === 0) return

      handleServicesSelect(productIds)
      const prices = {}
      const qty = {}
      if (lastBooking.services?.length) {
        lastBooking.services.forEach((s) => {
          const raw = s.serviceId ?? s.productId
          const p = findProduct(raw, s.serviceName)
          if (!p) return
          const sid = p._id
          if (s.actualPrice != null) {
            const base = p.price ?? 0
            prices[sid] = {
              basePrice: base,
              actualPrice: s.actualPrice
            }
          }
          if (s.quantity != null) qty[sid] = s.quantity
        })
      } else if (lastBooking.productId) {
        const p = findProduct(
          lastBooking.productId,
          lastBooking.productName
        )
        if (p) {
          const sid = p._id
          const base = p.price ?? 0
          const totalDiscount =
            lastBooking.discountedPrice ?? lastBooking.originalPrice
          if (totalDiscount != null) {
            prices[sid] = {
              basePrice: base,
              actualPrice: totalDiscount
            }
          }
          qty[sid] = 1
        }
      }
      const totalDiscount =
        lastBooking.discountedPrice ?? lastBooking.originalPrice
      setTimeout(() => {
        if (Object.keys(prices).length > 0) {
          setServicePrices((prev) => ({ ...prev, ...prices }))
        }
        if (Object.keys(qty).length > 0) {
          setServiceQuantities((prev) => ({ ...prev, ...qty }))
        }
        if (totalDiscount != null) {
          form.setFieldsValue({
            originalPrice: lastBooking.originalPrice ?? totalDiscount,
            discountedPrice: totalDiscount
          })
          setOriginalPrice(lastBooking.originalPrice ?? totalDiscount)
        }
      }, 0)
    } catch (e) {
      console.warn('帶入上一筆預約失敗', e)
    }
  }

  // 選擇客戶
  const selectCustomer = async (customer) => {
    const displayData = getCustomerDisplayData(customer)
    const customerName =
      displayData.name && displayData.name !== '未知' ? displayData.name : ''
    const customerPhone =
      displayData.phone && displayData.phone !== '未提供'
        ? displayData.phone
        : ''
    const customerEmail =
      displayData.email && displayData.email !== '未知'
        ? displayData.email
        : ''
    const customerId = customer._id || customer.customerId || null

    setCustomerNameValue(customerName)
    form.setFieldsValue({
      userName: customerName,
      phone: customerPhone,
      email: customerEmail,
      customerId: customerId,
      customerType: 'existing' // 從顧客名單選取的人自動帶入舊客戶
    })
    setShowCustomerList(false)
    setCustomers([])

    // 選擇客戶後詢問是否帶入該客戶的上一筆預約
    if (customerId) {
      const lastBooking = await fetchLastBookingByCustomerId(customerId)
      if (lastBooking) {
        Modal.confirm({
          title: '是否帶入上一筆預約？',
          content:
            '可將上一筆預約的設計師、服務與金額帶入表單（不含日期時間），方便快速建立類似預約。',
          okText: '帶入',
          cancelText: '不帶入',
          getContainer: () => document.body,
          onOk: () => {
            applyLastBookingToForm(lastBooking)
          }
        })
      }
    }
  }

  // 處理輸入變更
  const handleInputChange = (field, value) => {
    // 更新表單值
    form.setFieldsValue({ [field]: value })

    // 只有客戶姓名欄位才觸發搜尋
    if (field === 'userName') {
      // 只有在輸入法組合結束後才觸發搜尋
      if (!isComposing) {
        searchCustomers(value)
      }
    }
  }

  // 點擊外部關閉搜索結果
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showCustomerList &&
        !event.target.closest('.customer-search-container')
      ) {
        setShowCustomerList(false)
      }
    }

    if (showCustomerList) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showCustomerList])

  const renderDatePicker = () => {
    if (isMobile) {
      return (
        <>
          <MobileDatePicker
            visible={visible}
            onClose={() => setVisible(false)}
            precision='minute'
            filter={{ minute: (val) => val % 15 === 0 }}
            value={form.getFieldValue('date')?.toDate()}
            onConfirm={(val) => {
              const newDate = dayjs(val)
              const dateStr = newDate.format('YYYY-MM-DD HH:mm')
              if (unavailableTimeSlots.some((slot) => slot === dateStr)) {
                message.warning('選擇的時間不可預約，請選擇其他時間')
                return
              }
              form.setFieldsValue({ date: newDate })
              setVisible(false)
            }}
          />
          <div
            onClick={() => setVisible(true)}
            style={{
              cursor: 'pointer',
              padding: '4px 11px',
              border: '1px solid #d9d9d9',
              borderRadius: '2px',
              backgroundColor: '#fff'
            }}
          >
            {form.getFieldValue('date')
              ? dayjs(form.getFieldValue('date')).format('YYYY-MM-DD HH:mm')
              : '選擇日期和時間'}
          </div>
        </>
      )
    } else {
      return (
        <DatePicker
          showTime={{ format: 'HH:mm', minuteStep: 15 }}
          format='YYYY-MM-DD HH:mm'
          style={{ width: '100%' }}
          disabledDate={() => false}
          disabledTime={disabledDateTime}
          placeholder='選擇日期和時間'
        />
      )
    }
  }

  // 在 Appointments 組件內部添加這個函數
  const getStylistAvatar = (stylistId) => {
    const stylist = employees.find((emp) => emp._id === stylistId)
    return stylist ? stylist.avatar : null
  }

  /**
   * 週／日視圖的預約卡片。
   *
   * 這兩個視圖一格就是一個時段，橫向空間比月視圖寬得多，塞得下的資訊也多得多 ——
   * 月視圖那種「時間 客名 服務」擠成一行、超過就 ... 掉的排法在這裡是浪費版面，
   * 而店家最常需要當場確認的「這節是誰的客人、誰做」反而看不到。
   *
   * 依預約長度分四級，短預約不會被硬塞成三行而糊掉：
   *   60 分以上：時間區間／客名／服務／設計師（四行）
   *   40–59 分：時間＋客名／服務／設計師
   *   25–39 分：時間＋客名／設計師 · 服務
   *   25 分以下：全部收成一行
   *
   * 設計師沒指定時顯示「待指派」並換成警示色 —— 客人可以選「不指定人員」，
   * 這些單子需要店家回頭指派，在日曆上就要看得出來，不能跟已排好的長一樣。
   */
  const renderTimeGridEventContent = (eventInfo, isDayView) => {
    const { event } = eventInfo
    const { backgroundColor, textColor } = getStatusColor(
      event.extendedProps.status
    )

    const start = event.start ? dayjs(event.start).tz(bizTz) : null
    const end = event.end ? dayjs(event.end).tz(bizTz) : null
    const durationMin =
      start && end ? Math.max(0, end.diff(start, 'minute')) : 60
    /**
     * 週視圖只寫開始時間。
     *
     * 一週七欄，每欄放不下「09:00–12:00」這種區間，寫了就是把客人名字
     * 擠掉；結束時間本來就能從卡片的高度看出來，不必再用文字講一次。
     * 日視圖只有一欄、寬度充裕，維持完整區間。
     */
    const timeText = start
      ? isDayView && end
        ? `${start.format('HH:mm')}–${end.format('HH:mm')}`
        : start.format('HH:mm')
      : ''

    const userName = event.extendedProps.userName || ''
    const serviceName = event.extendedProps.serviceName || ''
    const stylistName = event.extendedProps.stylistName || ''
    const unassigned = !stylistName
    const stylistText = stylistName || '待指派'

    /**
     * 行數不能只看「預約幾分鐘」，要看這張卡實際有多高。
     * 平板的列高比桌機矮（34px vs 40px），同樣是 30 分鐘的預約，
     * 桌機放得下兩行、平板只放得下一行 —— 用分鐘數決定就會在平板上
     * 把兩行硬塞進一行的空間，字被切掉、行距糊在一起。
     */
    const isNarrow = windowWidth <= NARROW_BREAKPOINT
    const slotHeight = isNarrow
      ? TIMEGRID_SLOT_HEIGHT.narrow
      : TIMEGRID_SLOT_HEIGHT.desktop
    const baseFontSize = isDayView
      ? isNarrow
        ? 12
        : 12.5
      : isNarrow
        ? 11
        : 11.5
    const lineHeight = isNarrow ? 1.3 : 1.35
    const verticalPadding = isNarrow ? 4 : 6

    // FullCalendar 會給短事件最低 26px（eventMinHeight），比照計算
    const cardHeight = Math.max(26, (durationMin / 30) * slotHeight)
    const usableHeight = cardHeight - verticalPadding
    const lineBox = baseFontSize * lineHeight
    // 留 2px 餘裕：剛好塞滿的兩行在視覺上就是「擠」，寧可少一行也不要貼邊
    const maxLines = Math.max(1, Math.floor((usableHeight - 2) / lineBox))

    const tier =
      maxLines >= 4
        ? 'full'
        : maxLines === 3
          ? 'medium'
          : maxLines === 2
            ? 'compact'
            : 'mini'
    /**
     * 週視圖用 clip 而不是 ellipsis：欄位窄，幾乎每一行都會超出，
     * 滿畫面的「...」讀起來比被切一半的字還吵，而且省略號本身也佔掉
     * 兩三個字的寬度。日視圖夠寬、極少截斷，維持省略號比較有禮貌。
     */
    const rowStyle = {
      overflow: 'hidden',
      textOverflow: isDayView ? 'ellipsis' : 'clip',
      whiteSpace: 'nowrap'
    }
    const stylistStyle = {
      ...rowStyle,
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontWeight: 600,
      ...(unassigned
        ? { color: '#a8541c' }
        : { opacity: 0.85 })
    }

    const stylistBlock = (
      <span style={stylistStyle}>
        <UserOutlined style={{ fontSize: baseFontSize - 1, flex: 'none' }} />
        <span style={rowStyle}>{stylistText}</span>
      </span>
    )

    const fullTitle = [timeText, userName, serviceName, stylistText]
      .filter(Boolean)
      .join('  ')

    return (
      <div
        className='booking-card'
        style={{
          backgroundColor,
          color: textColor,
          borderRadius: 6,
          borderLeft: `3px solid ${textColor}`,
          fontSize: baseFontSize,
          lineHeight,
          padding:
            tier === 'mini'
              ? `${Math.max(1, verticalPadding / 2 - 1)}px 5px`
              : `${verticalPadding / 2}px 6px`,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: tier === 'full' ? 1 : 0
        }}
        title={fullTitle}
      >
        {tier === 'full' && (
          <>
            <b style={{ ...rowStyle, fontVariantNumeric: 'tabular-nums' }}>
              {timeText}
            </b>
            {userName && (
              <span style={{ ...rowStyle, fontWeight: 600 }}>{userName}</span>
            )}
            {serviceName && (
              <span
                className={isDayView ? 'booking-card-service' : undefined}
                style={{ ...rowStyle, opacity: 0.9 }}
              >
                {serviceName}
              </span>
            )}
            {stylistBlock}
          </>
        )}

        {tier === 'medium' && (
          <>
            <span style={rowStyle}>
              <b style={{ fontVariantNumeric: 'tabular-nums' }}>{timeText}</b>
              {userName && (
                <span style={{ fontWeight: 600 }}> {userName}</span>
              )}
            </span>
            {serviceName && (
              <span style={{ ...rowStyle, opacity: 0.9 }}>{serviceName}</span>
            )}
            {stylistBlock}
          </>
        )}

        {tier === 'compact' && (
          <>
            <span style={rowStyle}>
              <b style={{ fontVariantNumeric: 'tabular-nums' }}>{timeText}</b>
              {userName && (
                <span style={{ fontWeight: 600 }}> {userName}</span>
              )}
            </span>
            {isDayView ? (
              <span style={{ ...rowStyle, display: 'flex', gap: 4 }}>
                {stylistBlock}
                {serviceName && (
                  <span style={{ ...rowStyle, opacity: 0.85 }}>
                    · {serviceName}
                  </span>
                )}
              </span>
            ) : (
              stylistBlock
            )}
          </>
        )}

        {tier === 'mini' && (
          <span style={{ ...rowStyle, display: 'flex', gap: 4 }}>
            <b style={{ flex: 'none', fontVariantNumeric: 'tabular-nums' }}>
              {timeText}
            </b>
            {userName && (
              <span style={{ ...rowStyle, fontWeight: 600 }}>{userName}</span>
            )}
            <span
              style={{
                ...rowStyle,
                flex: 'none',
                fontWeight: 600,
                ...(unassigned ? { color: '#a8541c' } : { opacity: 0.85 })
              }}
            >
              · {stylistText}
            </span>
          </span>
        )}
      </div>
    )
  }

  const renderEventContent = (eventInfo) => {
    const { event } = eventInfo

    // 休假事件：跟預約同一條時間軸，但用自己的配色與文案，
    // 且不能走 getStatusColor（休假沒有預約狀態）
    if (event.extendedProps?.type === 'dayoff') {
      // 優先用建立事件時算好的完整區間（10:00-12:00），
      // 沒有才退回 FullCalendar 的預設（只有開始時間）
      const timeText = event.extendedProps.timeText || eventInfo.timeText || ''
      // 週視圖一欄只有百來 px，時間和名字並排一定有一個被吃掉；
      // 這裡格子夠高，改成上下兩行，跟預約卡片同一套排法
      const stackedForWeek = eventInfo.view?.type === 'timeGridWeek'
      return (
        <div
          className='dayoff-event'
          style={{
            backgroundColor: event.backgroundColor,
            color: event.textColor,
            borderLeft: `3px solid ${event.textColor}`,
            borderRadius: '5px',
            fontSize: '0.8em',
            padding: '2px 4px',
            display: 'flex',
            flexDirection: stackedForWeek ? 'column' : 'row',
            alignItems: stackedForWeek ? 'stretch' : 'center',
            gap: stackedForWeek ? '0' : '4px',
            lineHeight: stackedForWeek ? 1.3 : undefined,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: stackedForWeek ? 'clip' : 'ellipsis',
            width: '100%',
            height: '100%',
            boxSizing: 'border-box'
          }}
          title={`${timeText} ${event.title}`.trim()}
        >
          {/* 時間是重點，寬度不讓步；名字太長時才截斷 */}
          {timeText && <b style={{ flex: 'none' }}>{timeText}</b>}
          <span
            style={{
              flex: stackedForWeek ? 'none' : '1 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: stackedForWeek ? 'clip' : 'ellipsis'
            }}
          >
            {event.title}
          </span>
        </div>
      )
    }

    // 週／日視圖版面寬鬆，交給專用的多行卡片
    const viewType = eventInfo.view?.type || ''
    if (viewType === 'timeGridWeek' || viewType === 'timeGridDay') {
      return renderTimeGridEventContent(eventInfo, viewType === 'timeGridDay')
    }

    const { backgroundColor, textColor } = getStatusColor(
      event.extendedProps.status
    )

    const timeText = eventInfo.timeText || ''
    const userName = event.extendedProps.userName || ''
    let serviceName = event.extendedProps.serviceName || ''

    // 计算可用空间：时间 + 用户名 + 分隔符大约占用 15-20 个字符
    // 如果服务名称太长，直接截断并添加省略号
    const maxServiceNameLength = 35 // 服务名称最大显示长度
    const displayServiceName =
      serviceName.length > maxServiceNameLength
        ? serviceName.substring(0, maxServiceNameLength) + '...'
        : serviceName

    return (
      <div
        style={{
          backgroundColor: backgroundColor,
          color: textColor,
          borderRadius: '5px',
          fontSize: '0.8em',
          padding: '2px 4px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%',
          height: '100%',
          boxSizing: 'border-box'
        }}
        title={`${timeText} ${userName} ${serviceName}`.trim()} // 完整内容显示在 tooltip
      >
        {timeText && <b>{timeText}</b>}
        {userName && <span> {userName}</span>}
        {displayServiceName && <span> {displayServiceName}</span>}
      </div>
    )
  }

  // 事件挂载后确保样式正确应用
  const handleEventDidMount = (info) => {
    const { el } = info
    // 确保事件容器有正确的宽度限制
    if (el) {
      el.style.maxWidth = '100%'
      el.style.overflow = 'hidden'
      el.style.boxSizing = 'border-box'
    }

    // 确保内部容器也有正确的样式
    const eventMain = el?.querySelector('.fc-event-main')
    if (eventMain) {
      eventMain.style.maxWidth = '100%'
      eventMain.style.overflow = 'hidden'
      eventMain.style.boxSizing = 'border-box'
    }
  }

  /* ---------- 預約 hover 預覽 ---------- */

  /**
   * 進場延遲 180ms 才顯示。
   * 沒有延遲的話，滑鼠只是掃過日曆就會一路彈出十幾張卡，很吵；
   * 有意停留才看得到，跟一般 tooltip 的手感一致。
   */
  const handleEventMouseEnter = (info) => {
    if (isMobile) return
    // 休假／店休那種事件沒有預約資料，不需要預覽
    if (!info.event?.extendedProps?.userName) return

    clearTimeout(hoverTimerRef.current)
    const { clientX, clientY } = info.jsEvent || {}
    hoverTimerRef.current = setTimeout(() => {
      setHoverPreview({
        event: info.event,
        x: clientX ?? 0,
        y: clientY ?? 0
      })
    }, 180)
  }

  const handleEventMouseLeave = () => {
    clearTimeout(hoverTimerRef.current)
    setHoverPreview(null)
  }

  // 捲動或換頁時把卡片收掉，不然它會停在原地指著一筆已經捲走的預約
  useEffect(() => {
    if (!hoverPreview) return
    const close = () => {
      clearTimeout(hoverTimerRef.current)
      setHoverPreview(null)
    }
    window.addEventListener('scroll', close, true)
    window.addEventListener('wheel', close, { passive: true })
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('wheel', close)
    }
  }, [hoverPreview])

  useEffect(() => () => clearTimeout(hoverTimerRef.current), [])

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return {
          backgroundColor: '#f3e9e8', // 淡黄色半透明背景
          textColor: '#906856' // 深黄色文字
        }
      case 'confirmed':
        return {
          backgroundColor: '#b8d0c0', // 淡绿色半透明背景
          textColor: '#545e58' // 深绿色文字
        }
      case 'completed':
        return {
          backgroundColor: '#e8edf3', // 淡蓝色半透明背
          textColor: '#1f3963' // 深蓝色文字
        }
      case 'canceled':
        return {
          backgroundColor: '#fdd2cd', // 淡红色半透明背景
          textColor: '#903838' // 深红色文字
        }
      default:
        return {
          backgroundColor: 'rgba(255, 255, 255, 0.2)', // 默认白色半透明
          textColor: '#000000' // 黑色文字
        }
    }
  }

  const renderDrawerContent = () => {
    if (!selectedEvent) return null

    const { backgroundColor, textColor } = getStatusColor(
      selectedEvent.extendedProps.status
    )

    // 檢查是否有額外服務
    const hasAdditionalServices =
      selectedEvent.extendedProps.additionalServices &&
      selectedEvent.extendedProps.additionalServices.length > 0

    return (
      <>
        <StylistInfo>
          <StylistAvatar
            size='large'
            icon={<UserOutlined />}
            src={`${PHOTO_URL}${getStylistAvatar(
              selectedEvent.extendedProps.stylistId
            )}`}
          />
          <Space direction='vertical' size={0}>
            <Text strong>服務人員</Text>
            <Text>{selectedEvent.extendedProps.stylistName || '待指派'}</Text>
          </Space>
        </StylistInfo>
        <Divider style={{ margin: '5px 0' }} />
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
          <InfoItem>
            <IconWrapper>
              <CalendarOutlined />
            </IconWrapper>
            <Space direction='vertical' size={0}>
              <Text strong>日期和時間</Text>
              <Text>
                {dayjs(selectedEvent.start)
                  .tz(bizTz)
                  .format('YYYY年MM月DD日 HH:mm')}{' '}
                -{dayjs(selectedEvent.end).tz(bizTz).format('HH:mm')}
              </Text>
            </Space>
          </InfoItem>

          <InfoItem>
            <IconWrapper>
              <UserOutlined />
            </IconWrapper>
            <Space direction='vertical' size={0}>
              <Text strong>客戶</Text>
              <Text>{selectedEvent.extendedProps.userName}</Text>
            </Space>
          </InfoItem>

          <InfoItem>
            <IconWrapper>
              <PhoneOutlined />
            </IconWrapper>
            <Space direction='vertical' size={0}>
              <Text strong>聯絡電話</Text>
              <Text>{selectedEvent.extendedProps.phone || '未提供'}</Text>
            </Space>
          </InfoItem>

          <InfoItem>
            <IconWrapper>
              <MailOutlined />
            </IconWrapper>
            <Space direction='vertical' size={0}>
              <Text strong>電子郵件</Text>
              <Text>{selectedEvent.extendedProps.email || '未提供'}</Text>
            </Space>
          </InfoItem>

          <InfoItem>
            <IconWrapper>
              <ClockCircleOutlined />
            </IconWrapper>
            <Space direction='vertical' size={0}>
              <Text strong>服務</Text>
              <Text>
                {(() => {
                  // 如果有services數據，顯示包含數量的服務名稱
                  const services = selectedEvent.extendedProps?.services || []
                  if (services.length > 0) {
                    return services
                      .filter((s) => s.category !== 'additional')
                      .map((s) => {
                        const quantity = s.quantity || 1
                        return quantity > 1
                          ? `${s.serviceName || s.name} × ${quantity}`
                          : s.serviceName || s.name
                      })
                      .join('、')
                  }
                  // 否則使用title
                  return selectedEvent.title
                })()}
              </Text>
              {/* 顯示加選服務（如果有） */}
              {(() => {
                const services = selectedEvent.extendedProps?.services || []
                const additionalServices = services.filter(
                  (s) => s.category === 'additional'
                )
                if (additionalServices.length > 0) {
                  return (
                    <Text
                      style={{
                        color: '#6c757d',
                        fontSize: '13px',
                        marginTop: 4
                      }}
                    >
                      加選：{' '}
                      {additionalServices
                        .map((s) => {
                          const quantity = s.quantity || 1
                          return quantity > 1
                            ? `${s.serviceName || s.name} × ${quantity}`
                            : s.serviceName || s.name
                        })
                        .join('、')}
                    </Text>
                  )
                }
                return null
              })()}
            </Space>
          </InfoItem>

          {/* 顯示預約人數（如果大於1）
              用 Number(...) > 1 而不是 `x && x > 1`：participantCount 是 0
              的時候，JSX 的 && 會把那個 0 直接印在畫面上（服務項目下面憑空
              多出一個「0」，實際看到過）。判斷式一定要收斂成布林值。 */}
          {Number(selectedEvent.extendedProps?.participantCount) > 1 && (
              <InfoItem>
                <IconWrapper>
                  <UserOutlined />
                </IconWrapper>
                <Space direction='vertical' size={0}>
                  <Text strong>預約人數</Text>
                  <Text>{selectedEvent.extendedProps.participantCount} 人</Text>
                </Space>
              </InfoItem>
            )}

          {/* 金額資訊
              同樣要收斂成布林值：originalPrice 與 discountedPrice 都是 0 時
              （0 元服務，例如用堂票核銷的品項），`0 || 0` 的結果是 0，
              JSX 會把它當成要渲染的內容，畫面上就多一個孤零零的「0」。 */}
          {(Number(selectedEvent.extendedProps.originalPrice) > 0 ||
            Number(selectedEvent.extendedProps.discountedPrice) > 0) && (
            <InfoItem>
              <IconWrapper>
                <DollarOutlined />
              </IconWrapper>
              <Space direction='vertical' size={0}>
                <Text strong>金額</Text>
                <Space direction='vertical' size={4} style={{ width: '100%' }}>
                  {/* 0 元服務時 `x && (...)` 會把 0 印出來，要收斂成布林值 */}
                  {Number(selectedEvent.extendedProps.originalPrice) > 0 && (
                    <Text>
                      原價：$
                      {selectedEvent.extendedProps.originalPrice.toLocaleString()}
                    </Text>
                  )}
                  {selectedEvent.extendedProps.discountedPrice !== undefined &&
                    selectedEvent.extendedProps.discountedPrice !==
                      selectedEvent.extendedProps.originalPrice && (
                      <Text>
                        優惠價(結帳金額)：$
                        {selectedEvent.extendedProps.discountedPrice.toLocaleString()}
                      </Text>
                    )}
                  {selectedEvent.extendedProps.discountedPrice !== undefined &&
                    selectedEvent.extendedProps.discountedPrice ===
                      selectedEvent.extendedProps.originalPrice && (
                      <Text>
                        總金額：$
                        {selectedEvent.extendedProps.discountedPrice.toLocaleString()}
                      </Text>
                    )}
                  {selectedEvent.extendedProps.additionalServicesPrice > 0 && (
                    <Text type='secondary' style={{ fontSize: '12px' }}>
                      額外服務：$
                      {selectedEvent.extendedProps.additionalServicesPrice.toLocaleString()}
                    </Text>
                  )}
                </Space>
              </Space>
            </InfoItem>
          )}

          <InfoItem>
            <IconWrapper>
              <InfoCircleOutlined />
            </IconWrapper>
            <Space direction='vertical' size={0}>
              <Text strong>狀態</Text>
              <StyledTag
                textColor={textColor}
                backgroundColor={backgroundColor}
              >
                {(() => {
                  switch (selectedEvent.extendedProps.status) {
                    case 'pending':
                      return '等待確認'
                    case 'confirmed':
                      return '已確認'
                    case 'completed':
                      return '已完成'
                    case 'canceled':
                      return '已取消'
                    default:
                      return '未知狀態'
                  }
                })()}
              </StyledTag>
            </Space>
          </InfoItem>

          <InfoItem>
            <IconWrapper>
              <InfoCircleOutlined />
            </IconWrapper>
            <Space direction='vertical' size={0}>
              <Text strong>預約來源</Text>
              <Text>
                {selectedEvent.extendedProps.bookingSource === 'business'
                  ? '店家新增'
                  : '客人線上預約'}
              </Text>
            </Space>
          </InfoItem>

          {hasAdditionalServices && (
            <InfoItem>
              <IconWrapper>
                <PlusCircleOutlined />
              </IconWrapper>
              <Space direction='vertical' size={0}>
                <Text strong>額外服務</Text>
                {selectedEvent.extendedProps.additionalServices.map(
                  (service) => (
                    <div key={service._id || service.serviceId}>
                      <Space>
                        <Text>{service.serviceName}</Text>
                        <Text type='secondary'>
                          ${service.price} / {service.duration}分鐘
                        </Text>
                      </Space>
                    </div>
                  )
                )}
                {Number(
                  selectedEvent.extendedProps.additionalServicesPrice
                ) > 0 && (
                  <Text type='secondary'>
                    額外服務總價: $
                    {selectedEvent.extendedProps.additionalServicesPrice}
                  </Text>
                )}
              </Space>
            </InfoItem>
          )}

          {(() => {
            const { hasOrder, orderInfo } = getOrderInfo()
            return hasOrder && hasFeature('checkout') ? (
              <InfoItem>
                <IconWrapper>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                </IconWrapper>
                <Space direction='vertical' size={0}>
                  <Text strong>結帳狀態</Text>
                  <Space>
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: 'updateDate',
                            label: '改日期',
                            icon: <EditOutlined />,
                            onClick: ({ domEvent }) => {
                              domEvent.stopPropagation()
                              handleUpdateDate()
                            }
                          },
                          {
                            key: 'cancel',
                            label: '取消交易',
                            icon: <CloseCircleOutlined />,
                            onClick: ({ domEvent }) => {
                              domEvent.stopPropagation()
                              handleCancelOnly()
                            }
                          },
                          {
                            key: 'recheckout',
                            label: '重新結帳',
                            icon: <ReloadOutlined />,
                            onClick: ({ domEvent }) => {
                              domEvent.stopPropagation()
                              handleRecheckout()
                            }
                          }
                        ]
                      }}
                      trigger={['click']}
                      placement='bottomRight'
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentNode || document.body
                      }
                    >
                      <Tag
                        color='success'
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          margin: 0
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.8'
                          e.currentTarget.style.transform = 'scale(1.05)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                      >
                        已結帳{' '}
                        <DownOutlined style={{ marginLeft: 4, fontSize: 10 }} />
                      </Tag>
                    </Dropdown>
                    {orderInfo?.orderId && (
                      <Text type='secondary' style={{ fontSize: 12 }}>
                        訂單: {orderInfo.orderId}
                      </Text>
                    )}
                  </Space>
                </Space>
              </InfoItem>
            ) : hasOrder ? (
              <InfoItem>
                <IconWrapper>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                </IconWrapper>
                <Space direction='vertical' size={0}>
                  <Text strong>結帳狀態</Text>
                  <Space>
                    <Tag color='success'>已結帳</Tag>
                    {orderInfo?.orderId && (
                      <Text type='secondary' style={{ fontSize: 12 }}>
                        訂單: {orderInfo.orderId}
                      </Text>
                    )}
                  </Space>
                </Space>
              </InfoItem>
            ) : null
          })()}

          {/* 查看更多按鈕 */}
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: '1px solid #f0f0f0'
            }}
          >
            <Button
              type='primary'
              icon={<EyeOutlined />}
              block
              onClick={async () => {
                if (selectedEvent?.id) {
                  try {
                    // 獲取完整的預約數據
                    const response = await api.get(
                      `/api/business/bookings/${selectedEvent.id}`
                    )
                    if (response.data?.success && response.data?.data) {
                      navigate(`/bookings/${selectedEvent.id}`, {
                        state: { booking: response.data.data }
                      })
                      handleCloseDrawer()
                    } else {
                      message.error('無法獲取預約詳情')
                    }
                  } catch (error) {
                    console.error('獲取預約詳情失敗:', error)
                    message.error('無法獲取預約詳情')
                  }
                }
              }}
              style={{
                height: '40px',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              查看更多
            </Button>
          </div>
        </Space>
      </>
    )
  }

  const renderAddForm = () => (
    <Form
      form={form}
      layout='vertical'
      initialValues={{
        ...(selectedDate ? { date: selectedDate } : {}),
        originalPrice: 0,
        discountedPrice: 0,
        customerType: 'new',
        customerSource: 'none',
        notifyCustomer: true,
        // 後台建立的預約預設不收定金：電話／現場客多半當場付清，
        // 而 'unpaid' 會讓這筆預約落入自動取消排程、且永遠不會自動完成
        depositStatus: 'not_required'
      }}
    >
      {/* 客戶資料區塊：與 BookingTable 一致，置於最上方 */}
      <Form.Item
        name='userName'
        label='客戶姓名'
        rules={[{ required: true, message: '請輸入客戶姓名' }]}
      >
        <div
          className='customer-search-container'
          style={{ position: 'relative' }}
        >
          <Input
            value={customerNameValue}
            onChange={(e) => {
              const value = e.target.value
              setCustomerNameValue(value)
              form.setFieldsValue({ userName: value })
              if (!isComposing) {
                searchCustomers(value)
              }
            }}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={(e) => {
              setIsComposing(false)
              const value = e.target.value
              searchCustomers(value)
            }}
            placeholder='輸入姓名搜索客戶'
          />
          {showCustomerList && customers.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                background: '#fff',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                maxHeight: '200px',
                overflowY: 'auto'
              }}
            >
              {customers.map((customer) => {
                const pu = resolvePlatformUser(customer)
                return (
                  <div
                    key={customer._id}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#f5f5f5'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#fff'
                    }}
                    onClick={() => selectCustomer(customer)}
                  >
                    <div style={{ fontWeight: 500, color: '#1d1d1f' }}>
                      {(() => {
                        const dn = getCustomerDisplayData(customer).name
                        return dn && dn !== '未知'
                          ? dn
                          : customer.userName || pu?.name || '未命名客戶'
                      })()}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      {customer.businessRelations?.[0]?.businessSpecificData
                        ?.phone ||
                        pu?.phone ||
                        customer.phone ||
                        ''}{' '}
                      {customer.email || pu?.email
                        ? `• ${customer.email || pu?.email}`
                        : ''}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </Form.Item>

      <Form.Item
        name='customerType'
        label='客戶類型'
        rules={[{ required: true, message: '請選擇客戶類型' }]}
      >
        <Select>
          <Select.Option value='new'>新客戶</Select.Option>
          <Select.Option value='existing'>舊客戶</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        name='customerSource'
        label='客戶來源'
        rules={[{ required: true, message: '請選擇客戶來源' }]}
      >
        <Select>
          <Select.Option value='none'>無</Select.Option>
          <Select.Option value='online'>網路</Select.Option>
          <Select.Option value='referral'>轉介</Select.Option>
          <Select.Option value='walk-in'>路過</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item name='phone' label='聯絡電話'>
        <Input
          onChange={(e) => form.setFieldsValue({ phone: e.target.value })}
          placeholder='請輸入聯絡電話'
        />
      </Form.Item>

      <Form.Item
        name='email'
        label='電子郵件'
        rules={[{ type: 'email', message: '請輸入有效的電子郵件地址' }]}
      >
        <Input placeholder='請輸入電子郵件' />
      </Form.Item>

      <Form.Item
        name='productIds'
        label='選擇服務（可多選）'
        rules={[
          { required: true, message: '請至少選擇一個服務' },
          {
            validator: (_, value) => {
              if (!value || value.length === 0) {
                return Promise.reject(new Error('請至少選擇一個服務'))
              }
              return Promise.resolve()
            }
          }
        ]}
      >
        <Select
          mode='multiple'
          showSearch
          placeholder='請選擇服務（可輸入關鍵字搜索）'
          optionFilterProp='label'
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={handleServicesSelect}
          style={{ width: '100%' }}
        >
          {products.map((product) => (
            <Select.Option
              key={product._id}
              value={product._id}
              label={`${product.name} - $${product.price} (${product.duration}分鐘)`}
            >
              {product.name} - ${product.price} ({product.duration}分鐘)
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {/* 顯示已選服務摘要與價格編輯 */}
      {selectedServices.length > 0 && (
        <div
          style={{
            marginTop: 8,
            padding: 12,
            background: '#f5f5f5',
            borderRadius: 6,
            fontSize: 13,
            marginBottom: 16
          }}
        >
          <div style={{ marginBottom: 12, fontWeight: 600 }}>
            已選服務 ({selectedServices.length} 項)：
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {selectedServices.map((service) => {
              const serviceId = service._id
              const basePrice = service.price || 0
              const currentPrice =
                servicePrices[serviceId]?.actualPrice ?? basePrice
              const priceInfo = servicePrices[serviceId] || {
                basePrice,
                actualPrice: basePrice
              }

              return (
                <div
                  key={service._id}
                  style={{
                    padding: 12,
                    background: '#fff',
                    borderRadius: 6,
                    border: '1px solid #d9d9d9'
                  }}
                >
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>
                    {service.name} ({service.duration}分鐘)
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      flexWrap: 'wrap'
                    }}
                  >
                    <span style={{ fontSize: 12, color: '#6c757d' }}>
                      原價: ${basePrice.toLocaleString()}
                    </span>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <span style={{ fontSize: 12, color: '#6c757d' }}>
                        數量:
                      </span>
                      <InputNumber
                        min={1}
                        value={serviceQuantities[serviceId] || 1}
                        onChange={(value) => {
                          const quantity = value || 1
                          setServiceQuantities((prev) => ({
                            ...prev,
                            [serviceId]: quantity
                          }))

                          // 重新計算總價（考慮數量）
                          const updatedPrices = { ...servicePrices }
                          const updatedQuantities = {
                            ...serviceQuantities,
                            [serviceId]: quantity
                          }
                          const totalBasePrice = selectedServices.reduce(
                            (sum, s) => {
                              const sBasePrice = s.price || 0
                              const sQuantity = updatedQuantities[s._id] || 1
                              return sum + sBasePrice * sQuantity
                            },
                            0
                          )
                          const totalActualPrice = selectedServices.reduce(
                            (sum, s) => {
                              const sPrice =
                                updatedPrices[s._id]?.actualPrice ??
                                s.price ??
                                0
                              const sQuantity = updatedQuantities[s._id] || 1
                              return sum + sPrice * sQuantity
                            },
                            0
                          )
                          form.setFieldsValue({
                            originalPrice: totalBasePrice,
                            discountedPrice: totalActualPrice
                          })
                          setOriginalPrice(totalBasePrice)
                        }}
                        style={{ width: 80 }}
                        size='small'
                      />
                    </div>
                    <InputNumber
                      min={0}
                      value={currentPrice}
                      onChange={(value) => {
                        const updatedPrices = {
                          ...servicePrices,
                          [serviceId]: {
                            basePrice: priceInfo.basePrice || basePrice,
                            actualPrice: value || basePrice
                          }
                        }
                        setServicePrices(updatedPrices)

                        // 重新計算原價（basePrice 總和 * 數量）和優惠價（actualPrice 總和 * 數量）
                        const totalBasePrice = selectedServices.reduce(
                          (sum, s) => {
                            const sBasePrice = s.price || 0
                            const sQuantity = serviceQuantities[s._id] || 1
                            return sum + sBasePrice * sQuantity
                          },
                          0
                        )
                        const totalActualPrice = selectedServices.reduce(
                          (sum, s) => {
                            const sPrice =
                              updatedPrices[s._id]?.actualPrice ?? s.price ?? 0
                            const sQuantity = serviceQuantities[s._id] || 1
                            return sum + sPrice * sQuantity
                          },
                          0
                        )
                        form.setFieldsValue({
                          originalPrice: totalBasePrice,
                          discountedPrice: totalActualPrice
                        })
                        setOriginalPrice(totalBasePrice)
                      }}
                      style={{ width: 140 }}
                      formatter={(value) =>
                        `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                      size='small'
                    />
                  </div>
                </div>
              )
            })}
          </div>
          <div
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: '1px solid #d9d9d9',
              color: '#666'
            }}
          >
            總價：$
            {selectedServices
              .reduce((sum, s) => {
                const sPrice = servicePrices[s._id]?.actualPrice ?? s.price ?? 0
                const sQuantity = serviceQuantities[s._id] || 1
                return sum + sPrice * sQuantity
              }, 0)
              .toLocaleString()}{' '}
            | 總時長：{' '}
            {selectedServices.reduce((sum, s) => {
              const sQuantity = serviceQuantities[s._id] || 1
              return sum + (s.duration || 0) * sQuantity
            }, 0)}{' '}
            分鐘
          </div>
        </div>
      )}

      {/* 自訂服務時長：有些客人需要比服務預設時長更久（或更短） */}
      {selectedServices.length > 0 && (
        <Form.Item label='服務時長'>
          <Checkbox
            checked={useCustomDuration}
            onChange={(e) => {
              const checked = e.target.checked
              setUseCustomDuration(checked)
              if (checked) {
                setCustomDuration(
                  customDuration ||
                    selectedServices.reduce((sum, s) => {
                      const sQuantity = serviceQuantities[s._id] || 1
                      return sum + (s.duration || 0) * sQuantity
                    }, 0) ||
                    60
                )
              }
            }}
          >
            自訂這筆預約的時長
          </Checkbox>
          {useCustomDuration ? (
            <div style={{ marginTop: 8 }}>
              <InputNumber
                value={customDuration}
                onChange={(value) => setCustomDuration(value)}
                min={5}
                max={1440}
                step={5}
                addonAfter='分鐘'
                style={{ width: '100%', maxWidth: 200 }}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
                服務預設加總{' '}
                {selectedServices.reduce((sum, s) => {
                  const sQuantity = serviceQuantities[s._id] || 1
                  return sum + (s.duration || 0) * sQuantity
                }, 0)}{' '}
                分鐘，取消勾選即還原
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
              不勾選則依服務項目時長自動加總
            </div>
          )}
        </Form.Item>
      )}

      {/* 單筆預約人數上限=1 時隱藏人數欄位（固定 1 位） */}
      {maxParticipants > 1 && (
        <Form.Item
          name='participantCount'
          label='預約人數'
          rules={[
            { required: true, message: '請輸入預約人數' },
            { type: 'number', min: 1, message: '預約人數至少為1人' },
            {
              type: 'number',
              max: maxParticipants,
              message: `預約人數最多為${maxParticipants}人（可於預約設定調整）`
            }
          ]}
          initialValue={1}
        >
          <InputNumber
            min={1}
            max={maxParticipants}
            precision={0}
            style={{ width: '100%' }}
            placeholder='請輸入預約人數'
            onChange={(value) => {
              setParticipantCount(value || 1)
              form.setFieldsValue({ participantCount: value || 1 })
            }}
          />
        </Form.Item>
      )}

      <Form.Item
        name='stylistId'
        label='選擇造型師'
        rules={[{ required: true, message: '請先選擇造型師' }]}
        extra={
          selectedStylistId
            ? '系統已根據員工和店家休假自動禁用不可預約的時間'
            : '可先選擇日期時間或先選擇造型師'
        }
      >
        <Select
          placeholder='請選擇造型師'
          onChange={handleStylistChange}
          value={selectedStylistId}
        >
          {stylists.map((stylist) => (
            <Select.Option key={stylist._id} value={stylist._id}>
              {stylist.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name='date'
        label='預約日期和時間'
        rules={[
          { required: true, message: '請選擇預約日期和時間' },
          () => ({
            validator(_, value) {
              if (!selectedStylistId) {
                return Promise.reject(new Error('請先選擇造型師'))
              }
              return Promise.resolve()
            }
          })
        ]}
        extra={
          selectedStylistId
            ? '系統已根據員工和店家休假自動禁用不可預約的時間'
            : '可先選擇日期時間，選擇造型師後會自動禁用該造型師不可預約的時間'
        }
      >
        {renderDatePicker()}
      </Form.Item>

      {/* 店家沒啟用定金功能就別問了，值維持 initialValues 的 not_required */}
      {depositEnabled && (
        <Form.Item
          name='depositStatus'
          label='定金狀態'
          rules={[{ required: true, message: '請選擇定金狀態' }]}
        >
          <Select placeholder='請選擇定金狀態'>
            <Select.Option value='paid'>已付款</Select.Option>
            <Select.Option value='unpaid'>待付款</Select.Option>
            <Select.Option value='not_required'>不需定金</Select.Option>
          </Select>
        </Form.Item>
      )}

      <Form.Item name='note' label='備註'>
        <Input.TextArea />
      </Form.Item>

      <Form.Item name='notifyCustomer' valuePropName='checked'>
        <Checkbox>通知客戶（LINE／Email）</Checkbox>
      </Form.Item>

      <Form.Item
        name='originalPrice'
        label='原價'
        rules={[{ required: true, message: '請輸入原價' }]}
      >
        <InputNumber
          style={{ width: '100%' }}
          formatter={(value) =>
            `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          }
          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
          disabled
        />
      </Form.Item>

      <Form.Item
        name='discountedPrice'
        label='優惠價(結帳金額)'
        rules={[{ required: true, message: '請輸入優惠價' }]}
      >
        <InputNumber
          style={{ width: '100%' }}
          formatter={(value) =>
            `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          }
          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
        />
      </Form.Item>
    </Form>
  )

  /**
   * 真正交給 FullCalendar 的 events。
   *
   * ⚠️ 一定要 useMemo。FullCalendar v6 是用「陣列的參照」判斷事件來源有沒有變，
   * 只要每次 render 都給它一個新陣列，它就會把所有事件砍掉重畫（eventContent /
   * eventDidMount 全部重跑）。而這個頁面 hover 預覽、視窗寬度、日期跳轉都會觸發
   * render —— 滑過任何一張預約卡都在重畫整個日曆，就是店家說的「一直閃」。
   */
  const calendarEvents = useMemo(
    () => [
      ...filteredEvents.map((event) => ({
        ...event,
        backgroundColor: getStatusColor(event.extendedProps.status)
          .backgroundColor,
        textColor: getStatusColor(event.extendedProps.status).textColor
      })),
      // 有時段的休假：跟預約一起按時間排序（顏色自帶，不走 getStatusColor）
      ...visibleDayOffEvents
    ],
    // getStatusColor 只是靜態色票查表，不需要進依賴（每次 render 都是新函式，
    // 放進來等於這個 useMemo 永遠失效）
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredEvents, visibleDayOffEvents]
  )

  return (
    <StyledCalendar>
      <CalendarWrapper>
        {/* 日期跳轉：選擇器的模式跟著目前視圖走，選月視圖時挑月、
            週視圖挑週、日視圖挑日，避免選了單日卻停在月曆上 */}
        <DateJumpBar>
          <span className='jump-label'>
            <CalendarOutlined />
            跳到
          </span>
          <DatePicker
            allowClear={false}
            inputReadOnly
            value={calendarDate}
            onChange={handleJumpToDate}
            /* 週視圖刻意也用日期選擇器而不是 antd 的 week picker：
               店家想的是「跳到 9/26 那天」，不是「第 39 週」，而且 week
               picker 的 w 格式需要 dayjs 的 weekOfYear／advancedFormat
               外掛，這個專案沒有載。選任一天就會跳到那天所在的週。 */
            picker={calendarView === 'dayGridMonth' ? 'month' : 'date'}
            format={
              calendarView === 'dayGridMonth' ? 'YYYY 年 M 月' : 'YYYY-MM-DD'
            }
            style={{ minWidth: 170 }}
          />
          <Button
            className='today-btn'
            type='text'
            onClick={() => calendarRef.current?.getApi()?.today()}
          >
            回到今天
          </Button>
        </DateJumpBar>
        <CalendarSurface>
        <FullCalendar
          ref={calendarRef}
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            interactionPlugin,
            momentTimezonePlugin
          ]}
          initialView='dayGridMonth'
          locale='zh-tw'
          timeZone={bizTz}
          events={calendarEvents}
          /* 「今天」移到上方的日期跳轉列 —— 那裡已經有一顆一模一樣的
             「回到今天」，兩顆功能相同的按鈕上下相隔 40px 是多餘的 */
          headerToolbar={{
            left: 'prev,next',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          buttonText={buttonText}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          dayCellContent={renderDayCellContent}
          datesSet={handleDatesSet}
          titleFormat={titleFormat}
          dayMaxEvents={5}
          /* 週／日視圖：一格 30 分、每小時標一次時間，列高由 CSS 拉高，
             卡片才放得下客名、服務與設計師 */
          slotDuration='00:30:00'
          slotLabelInterval='01:00'
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
          /* 同時段的預約並排而不是疊在一起 —— 兩個人同時開始的單子，
             疊起來只看得到上面那張 */
          slotEventOverlap={false}
          /* 現在時間線：日視圖當天用得最多，一眼知道進度 */
          nowIndicator
          /* 15 分鐘的短單也要留得住一行字 */
          eventMinHeight={26}
          eventShortHeight={44}
          /* 時間軸範圍：預設依營業時間收合，並自動撐開到蓋住實際的預約／班表。
             店家可在「預約設定」改成畫滿 24 小時。 */
          slotMinTime={timelineBounds.min}
          slotMaxTime={timelineBounds.max}
          scrollTime={calendarScrollTime}
          expandRows
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
          eventContent={renderEventContent}
          eventDidMount={handleEventDidMount}
          /* 滑過預約就先看到客人與備註，不用每一筆都點開 Modal */
          eventMouseEnter={handleEventMouseEnter}
          eventMouseLeave={handleEventMouseLeave}
          eventDisplay='block'
        />
        {isLoading && (
          <CalendarLoadingMask>
            <Spin size='large' tip='載入預約中…' />
          </CalendarLoadingMask>
        )}
        </CalendarSurface>

        {hoverPreview &&
          (() => {
            const ep = hoverPreview.event.extendedProps || {}
            const profile = ep.customerProfile || {}
            const { backgroundColor, textColor } = getStatusColor(ep.status)

            const start = ep.bookingTime || ''
            /* 用 T 分隔＋dayjs.tz，跟上面組 event.start 的寫法一致。
               這支檔案沒有 extend customParseFormat，帶 format 字串的
               三參數寫法在這裡不會生效（Safari 還會解析失敗） */
            const finish =
              ep.bookingDate && start
                ? dayjs
                    .tz(`${ep.bookingDate}T${start}`, bizTz)
                    .add(ep.duration || 0, 'minute')
                    .format('HH:mm')
                : ''
            // 這幾個是 Mongo 的絕對時刻，要用店家時區顯示，
            // 否則台灣時間的凌晨會顯示成前一天
            const fmtDate = (v) => (v ? inTz(v).format('YYYY/MM/DD') : '—')

            /* 貼著游標放，靠近視窗右緣就翻到左邊；上下夾在可視範圍內，
               不然卡片會有一半被切在畫面外 */
            const CARD_W = 300
            const GAP = 14
            const vw = window.innerWidth
            const vh = window.innerHeight
            const left =
              hoverPreview.x + GAP + CARD_W > vw
                ? Math.max(8, hoverPreview.x - GAP - CARD_W)
                : hoverPreview.x + GAP
            const top = Math.min(
              Math.max(12, hoverPreview.y - 40),
              Math.max(12, vh - 360)
            )

            return (
              <HoverCard style={{ left, top }}>
                <div className='hc-head'>
                  <span className='hc-name'>{ep.userName || '未命名'}</span>
                  {profile.blacklist && <span className='hc-black'>黑名單</span>}
                  <span
                    className='hc-status'
                    style={{ background: backgroundColor, color: textColor }}
                  >
                    {STATUS_LABEL[ep.status] || '未知狀態'}
                  </span>
                </div>

                <div className='hc-row'>
                  <span className='hc-k'>預約時間</span>
                  <span className='hc-v'>
                    {ep.bookingDate} {start}
                    {finish ? ` ~ ${finish}` : ''}
                    {ep.duration ? `（${ep.duration} 分）` : ''}
                  </span>
                </div>
                <div className='hc-row'>
                  <span className='hc-k'>服務</span>
                  <span className='hc-v'>{ep.serviceName || '—'}</span>
                </div>
                <div className='hc-row'>
                  <span className='hc-k'>服務人員</span>
                  <span className='hc-v'>{ep.stylistName || '待指派'}</span>
                </div>
                {ep.participantCount > 1 && (
                  <div className='hc-row'>
                    <span className='hc-k'>預約人數</span>
                    <span className='hc-v'>{ep.participantCount} 位</span>
                  </div>
                )}

                <div className='hc-sep' />
                <div className='hc-row'>
                  <span className='hc-k'>手機</span>
                  <span className='hc-v'>{ep.phone || '—'}</span>
                </div>
                <div className='hc-row'>
                  <span className='hc-k'>生日</span>
                  <span className='hc-v'>{fmtDate(ep.customerBirthdate)}</span>
                </div>
                <div className='hc-row'>
                  <span className='hc-k'>首次到店</span>
                  <span className='hc-v'>{fmtDate(profile.firstVisit)}</span>
                </div>
                <div className='hc-row'>
                  <span className='hc-k'>最近到店</span>
                  <span className='hc-v'>{fmtDate(profile.lastVisit)}</span>
                </div>
                {profile.membershipLevel ? (
                  <div className='hc-row'>
                    <span className='hc-k'>會員等級</span>
                    <span className='hc-v'>{profile.membershipLevel}</span>
                  </div>
                ) : null}

                {profile.notes ? (
                  <>
                    <div className='hc-sep' />
                    <span className='hc-k'>顧客備註</span>
                    <div className='hc-note'>{profile.notes}</div>
                  </>
                ) : null}

                {ep.note ? (
                  <>
                    <div className='hc-sep' />
                    <span className='hc-k'>預約備註</span>
                    <div className='hc-note'>{ep.note}</div>
                  </>
                ) : null}

                {profile.tags?.length ? (
                  <div className='hc-tags'>
                    {profile.tags.slice(0, 6).map((t) => (
                      <span className='hc-tag' key={t}>
                        {t}
                      </span>
                    ))}
                  </div>
                ) : null}

                <div className='hc-hint'>點擊預約可查看完整內容與操作</div>
              </HoverCard>
            )
          })()}
        <CustomHeader>
          <StatusLegend>
            <StatusItem>
              <StatusDot style={{ backgroundColor: '#f3e9e8' }} />
              等待確認
            </StatusItem>
            <StatusItem>
              <StatusDot style={{ backgroundColor: '#b8d0c0' }} />
              已確認
            </StatusItem>
            <StatusItem>
              <StatusDot style={{ backgroundColor: '#e8edf3' }} />
              已完成
            </StatusItem>
            <StatusItem>
              <StatusDot style={{ backgroundColor: '#fdd2cd' }} />
              已取消
            </StatusItem>
          </StatusLegend>
          <EmployeeBar justify='start'>
            <Tooltip title='點擊頭像以篩選特定服務人員的預約'>
              <InfoCircleOutlined
                style={{
                  color: '#666',
                  fontSize: '16px',
                  marginRight: '8px',
                  cursor: 'pointer'
                }}
              />
            </Tooltip>
            <StyledAvatar
              key='all'
              icon={<PiUsersFill />}
              onClick={() => handleEmployeeFilter('all')}
              style={{
                border:
                  selectedEmployee === 'all' ? '2px solid #e36882' : 'none',
                backgroundColor: '#fff',
                color: '#666'
              }}
            />
            {employees.map((employee) => (
              <StyledAvatar
                key={employee._id}
                src={`${PHOTO_URL}${employee.avatar}`}
                onClick={() => handleEmployeeFilter(employee._id)}
                style={{
                  border:
                    selectedEmployee === employee._id
                      ? '2px solid #e36882'
                      : 'none'
                }}
              />
            ))}
          </EmployeeBar>
        </CustomHeader>
      </CalendarWrapper>
      <StyledDrawer
        open={isDrawerVisible}
        title='預約詳情'
        onClose={handleCloseDrawer}
        placement='right'
        closable={true}
        extra={
          hasFeature('checkout') &&
          (() => {
            const { hasOrder } = getOrderInfo()
            const bookingStatus = selectedEvent?.extendedProps?.status
            // 如果預約狀態是取消，不顯示結帳按鈕
            if (bookingStatus === 'canceled') {
              return null
            }
            return !hasOrder ? (
              <Button
                type='primary'
                icon={<DollarOutlined />}
                onClick={handleCheckout}
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
              >
                結帳
              </Button>
            ) : null
          })()
        }
      >
        {renderDrawerContent()}
      </StyledDrawer>

      {isMobile ? (
        <MobileAddDrawer
          open={isAddModalVisible}
          onClose={handleAddModalCancel}
          title='新增預約'
          placement='right'
          width='100%'
          footer={
            <Button onClick={handleAddModalOk} type='primary' block>
              確認新增
            </Button>
          }
        >
          {renderAddForm()}
        </MobileAddDrawer>
      ) : (
        <Drawer
          title='新增預約'
          open={isAddModalVisible}
          onClose={handleAddModalCancel}
          width={500}
          footer={
            <Space style={{ float: 'right' }}>
              <Button onClick={handleAddModalCancel}>取消</Button>
              <Button
                type='primary'
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                確認新增
              </Button>
            </Space>
          }
        >
          {renderAddForm()}
        </Drawer>
      )}

      {/* 改日期 Modal */}
      <Modal
        title='修改訂單日期'
        open={isUpdateDateModalVisible}
        onCancel={() => {
          setIsUpdateDateModalVisible(false)
          updateDateForm.resetFields()
        }}
        onOk={handleUpdateDateSubmit}
        okText='確認'
        cancelText='取消'
        width={isMobile ? '90%' : 500}
      >
        <Form
          form={updateDateForm}
          layout='vertical'
          initialValues={{
            date: null,
            startTime: null,
            endTime: null
          }}
        >
          <Form.Item
            name='date'
            label='日期'
            rules={[{ required: true, message: '請選擇日期' }]}
          >
            {isMobile ? (
              <>
                <MobileDatePicker
                  visible={updateDatePickerVisible}
                  onClose={() => setUpdateDatePickerVisible(false)}
                  precision='day'
                  value={updateDateForm.getFieldValue('date')?.toDate()}
                  onConfirm={(val) => {
                    updateDateForm.setFieldsValue({ date: dayjs(val) })
                    setUpdateDatePickerVisible(false)
                  }}
                />
                <div
                  onClick={() => setUpdateDatePickerVisible(true)}
                  style={{
                    cursor: 'pointer',
                    padding: '4px 11px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '2px',
                    backgroundColor: '#fff'
                  }}
                >
                  {updateDateForm.getFieldValue('date')
                    ? dayjs(updateDateForm.getFieldValue('date')).format(
                        'YYYY-MM-DD'
                      )
                    : '選擇日期'}
                </div>
              </>
            ) : (
              <DatePicker
                style={{ width: '100%' }}
                format='YYYY-MM-DD'
                placeholder='選擇日期'
              />
            )}
          </Form.Item>

          <Form.Item
            name='startTime'
            label='開始時間'
            rules={[
              { required: true, message: '請輸入開始時間' },
              {
                pattern: /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/,
                message: '時間格式必須為 HH:mm'
              }
            ]}
          >
            <Input placeholder='HH:mm (例如: 14:30)' />
          </Form.Item>

          <Form.Item
            name='endTime'
            label='結束時間（可選）'
            rules={[
              {
                pattern: /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/,
                message: '時間格式必須為 HH:mm'
              }
            ]}
          >
            <Input placeholder='HH:mm (例如: 16:30)' />
          </Form.Item>
        </Form>
      </Modal>

      {/* 休假時間區間詳情彈窗 */}
      <Modal
        title={
          dayOffDetail
            ? `${dayjs(dayOffDetail.dateKey).format('YYYY-MM-DD')} 休假時段`
            : '休假時段'
        }
        open={!!dayOffDetail}
        onCancel={() => setDayOffDetail(null)}
        footer={null}
      >
        {dayOffDetail?.info?.businessSegments?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, color: '#b65066', marginBottom: 6 }}>
              店休
            </div>
            {dayOffDetail.info.businessSegments.map((seg) => (
              <div key={seg.key} style={{ marginBottom: 4 }}>
                {formatDayOffSegment(seg.start, seg.end)}
              </div>
            ))}
          </div>
        )}

        {dayOffDetail?.info?.employeeSegments?.length > 0 &&
          Object.entries(
            dayOffDetail.info.employeeSegments.reduce((acc, seg) => {
              if (!acc[seg.name]) acc[seg.name] = []
              acc[seg.name].push(seg)
              return acc
            }, {})
          ).map(([name, segs]) => (
            <div key={name} style={{ marginBottom: 16 }}>
              <div
                style={{ fontWeight: 600, color: '#6b4fa3', marginBottom: 6 }}
              >
                {name}{' '}
                {[...new Set(segs.map((s) => s.typeName).filter(Boolean))].join(
                  '、'
                ) || '休假'}
              </div>
              {segs.map((seg) => (
                <div key={seg.key} style={{ marginBottom: 4 }}>
                  {formatDayOffSegment(seg.start, seg.end)}
                </div>
              ))}
            </div>
          ))}

        {!dayOffDetail?.info?.businessSegments?.length &&
          !dayOffDetail?.info?.employeeSegments?.length && (
            <div style={{ color: '#999' }}>無休假時段資料</div>
          )}
      </Modal>
    </StyledCalendar>
  )
}

export default Appointments
